// src/App.js
import React, { useState, useEffect, useRef } from 'react';
import './styles/theme.css';
import './styles/App.css';
import './styles/PartyButton.css';
import { Browser } from '@capacitor/browser';
import PartyButton   from './components/PartyButton';
import MapView       from './components/MapView';
import VenueCard     from './components/VenueCard';
import HeadlineFeed  from './components/HeadlineFeed';
import { pickFallback, DEFAULT_PHOTO_POOL } from './components/fallbacks';
import { InAppReview } from '@capacitor-community/in-app-review';
import ExploreSheet from "./components/ExploreSheet";
import PartyQuestSheet from "./components/PartyQuestSheet";
import './styles/EmojiRescue.css';
import { rescueEmoji } from './utils/emojiRescue';
import {
  getCategoryEndpoint,
  getCategoryModeLabel,
  getCategorySearchLabel,
  normalizeCategoryKey,
} from './config/categoryPresentation';
import marketConfig from './config/markets.json';

const isSafeImage = (url) => {
  if (typeof url !== 'string') return false;

  const s = url.trim().toLowerCase();

  if (!s) return false;

  return !(
    s.includes('.webp') ||
    s.includes('format=webp') ||
    s.includes('fm=webp') ||
    s.includes('auto=format') ||
    s.includes('chatgpt') ||
    s.includes('oaistatic')
  );
};

const pickSafeFallback = (seed = 0) => {
  for (let i = 0; i < 20; i++) {
    const img = pickFallback(seed + i);
    if (isSafeImage(img)) return img;
  }
  return '';
};

const imageSeedForVenue = (v, offset = 0) => {
  const s = String(v?.place_id || v?.placeId || v?.id || v?.name || offset);
  let h = 0;

  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  }

  return Math.abs(h + offset);
};

const needsFallbackImage = (url) => {
  const s = String(url || '').trim().toLowerCase();

  return (
    !s ||
    s === 'null' ||
    s === 'undefined' ||
    s === '/default-image.jpg' ||
    s.endsWith('/default-image.jpg') ||
    s.includes('/default-image.jpg') ||
    s.includes('undefined') ||
    s.includes('null')
  );
};

const withSafeVenueImage = (v, index = 0) => {
  if (!v) return v;

  if (!needsFallbackImage(v.image)) return v;

  return {
    ...v,
    image: pickSafeFallback(imageSeedForVenue(v, index)),
  };
};

const LAST_LOC_KEY = 'PB_LAST_LOC_V1';
const LAST_VENUE_KEY = 'PB_LAST_VENUE_V1';

function readLastVenue() {
  try {
    const raw = localStorage.getItem(LAST_VENUE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function saveLastVenue(v) {
  try {
    if (!v) return;
    localStorage.setItem(LAST_VENUE_KEY, JSON.stringify(v));
  } catch {}
}

const SAVED_VENUES_KEY = 'PB_SAVED_VENUES_V1';
const SAVED_EVENTS_KEY = 'PB_SAVED_EVENTS_V1';

function readSavedItems(key) {
  try {
    const raw = localStorage.getItem(key);

    if (!raw) return [];

    const parsed = JSON.parse(raw);

    return Array.isArray(parsed)
      ? parsed
      : [];
  } catch {
    return [];
  }
}

function writeSavedItems(key, items) {
  try {
    localStorage.setItem(
      key,
      JSON.stringify(
        Array.isArray(items)
          ? items
          : []
      )
    );
  } catch {}
}

function savedVenueId(venue) {
  return String(
    venue?.place_id ||
    venue?.placeId ||
    venue?.id ||
    venue?.name ||
    ''
  );
}

function savedEventId(event) {
  const source =
    String(event?.source || '').trim();

  const externalId =
    String(event?.externalId || '').trim();

  if (source && externalId) {
    return `${source}:${externalId}`;
  }

  return String(
    event?.id ||
    `${event?.name || 'event'}:${event?.startDateTime || ''}`
  );
}

function readLastLocation(maxAgeMs = 6 * 60 * 60 * 1000) {
  try {
    const raw = localStorage.getItem(LAST_LOC_KEY);
    if (!raw) return null;
    const j = JSON.parse(raw);
    const lat = Number(j?.latitude);
    const lng = Number(j?.longitude);
    const t = Number(j?.t || 0);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    if (t && Date.now() - t > maxAgeMs) return null;
    return { latitude: lat, longitude: lng };
  } catch {
    return null;
  }
}

function writeLastLocation(loc) {
  try {
    const lat = Number(loc?.latitude);
    const lng = Number(loc?.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    localStorage.setItem(
      LAST_LOC_KEY,
      JSON.stringify({ latitude: lat, longitude: lng, t: Date.now() })
    );
  } catch {}
}




// Treat Capacitor *or* the capacitor:// origin as native
const isNative =
  !!(window?.Capacitor?.isNativePlatform?.() ?? window?.Capacitor?.isNative) ||
  /^capacitor:\/\//i.test(window.location.origin);

// True only when running as a regular website build
const usingHttpOrigin = /^https?:\/\//i.test(window.location.origin);
const isWebPromoOnly = usingHttpOrigin && !isNative;
const APP_STORE_URL = "https://apps.apple.com/us/app/the-party-button/id6749190787";

// TestFlight staging builds supply this URL at build time.
const DEFAULT_BACKEND_BASE =
  (process.env.REACT_APP_PARTY_BACKEND_URL || '').trim().replace(/\/+$/, '') ||
  "https://partybutton-private-a027fa6fce78.herokuapp.com";




// ---- PB_MARKETS_V1 ----
// PB_OAKLAND_STAGING_V1: beta opt-in; committed market defaults remain unchanged.
const OAKLAND_BETA_ENABLED = ['1', 'true', 'yes', 'on'].includes(
  String(process.env.REACT_APP_OAKLAND_BETA || '').toLowerCase()
);
const MARKET_MAP = {
  ...(marketConfig?.markets || {}),
  ...(OAKLAND_BETA_ENABLED && marketConfig?.markets?.oakland
    ? { oakland: { ...marketConfig.markets.oakland, enabled: true } }
    : {}),
};
const DEFAULT_MARKET_ID = marketConfig?.defaultMarketId || 'sf';

function getMarketById(marketId) {
  const key = String(marketId || '').trim().toLowerCase();
  const market = MARKET_MAP[key];
  return market?.enabled ? market : null;
}

function getMarketForCoordinates(lat, lng) {
  const latitude = Number(lat);
  const longitude = Number(lng);

  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return null;
  }

  return (
    Object.values(MARKET_MAP).find((market) => {
      if (!market?.enabled || !market?.bounds) return false;
      const b = market.bounds;

      return (
        latitude >= Number(b.minLat) &&
        latitude <= Number(b.maxLat) &&
        longitude >= Number(b.minLng) &&
        longitude <= Number(b.maxLng)
      );
    }) || null
  );
}

const DEFAULT_MARKET =
  getMarketById(DEFAULT_MARKET_ID) ||
  Object.values(MARKET_MAP).find((market) => market?.enabled) ||
  null;

function isWithinSupportedMarket(lat, lng) {
  return Boolean(getMarketForCoordinates(lat, lng));
}
/**
 * Decide which backend URL to use.
 * Priority:
 *   1) Native (Capacitor): always DEFAULT_BACKEND_BASE
 *   2) Web query param:    ?backend=
 *   3) Web localStorage:   PARTY_BACKEND_URL
 *   4) Otherwise:          DEFAULT_BACKEND_BASE
 */
function resolveBackendBase() {
  // Native (or non-http origin): always use Heroku default so it "just works"
  if (isNative || !usingHttpOrigin) return DEFAULT_BACKEND_BASE;

  // Web: allow ?backend=... override
  try {
    const q = new URLSearchParams(window.location.search).get('backend');
    if (q) {
      const u = new URL(String(q).trim().replace('.-com', '.com'));
      if (/^https?:$/.test(u.protocol)) {
        const origin = u.origin.replace(/\/+$/, '');
        try {
          localStorage.setItem('PARTY_BACKEND_URL', origin);
        } catch {}
        return origin;
      }
    }
  } catch {}

  // Web: honor localStorage if present
  try {
    const raw =
      (typeof localStorage !== 'undefined' &&
        localStorage.getItem('PARTY_BACKEND_URL')) ||
      '';
    if (raw) {
      const u = new URL(String(raw).trim().replace('.-com', '.com'));
      if (/^https?:$/.test(u.protocol)) {
        return u.origin.replace(/\/+$/, '');
      }
    }
  } catch {}

  // Fallback
  return DEFAULT_BACKEND_BASE;
}

// Force the correct Heroku URL on native/web
const BACKEND_BASE = resolveBackendBase();
console.log('[Party] BACKEND_BASE:', BACKEND_BASE, 'origin:', window.location.origin, 'isNative:', !!window.Capacitor?.isNative);

const api = (p) => `${BACKEND_BASE}${p}`;
window.__PARTY_BACKEND__ = BACKEND_BASE;

async function fetchJSON(path, options = {}) {
  const primary = api(path);

  const doFetch = async (url) => {
    const timeoutController = new AbortController();
    const callerSignal = options?.signal;

    const onCallerAbort = () => timeoutController.abort();
    if (callerSignal) {
      if (callerSignal.aborted) timeoutController.abort();
      else callerSignal.addEventListener('abort', onCallerAbort, { once: true });
    }

      const timeout = setTimeout(() => timeoutController.abort(), 35000);

    try {
      const r = await fetch(url, {
        ...options,
        signal: timeoutController.signal,
      });

      if (r.ok) return await r.json();

      const err = new Error(`HTTP ${r.status}`);
      err.status = r.status;

      try {
        const ra = r.headers?.get?.('retry-after');
        if (ra) err.retryAfter = Number(ra) || ra;
      } catch {}

      try {
        err.bodyText = await r.text();
      } catch {}

      throw err;
    } finally {
      clearTimeout(timeout);
      if (callerSignal) {
        callerSignal.removeEventListener('abort', onCallerAbort);
      }
    }
  };

  try {
    return await doFetch(primary);
  } catch (err) {
    console.warn('[Party] primary fetch failed:', err?.message || err);

    if (BACKEND_BASE !== DEFAULT_BACKEND_BASE) {
      const alt = `${DEFAULT_BACKEND_BASE}${path}`;
      return await doFetch(alt);
    }

    throw err;
  }
}
const safeAreaStyle = {
  paddingTop: 'calc(12px + env(safe-area-inset-top))',
  paddingRight: 'calc(12px + env(safe-area-inset-right))',
  paddingBottom: 'calc(12px + env(safe-area-inset-bottom))',
  paddingLeft: 'calc(12px + env(safe-area-inset-left))',
  minHeight: '100dvh',
};




/* distance helpers */
const toRad = (d) => (d * Math.PI) / 180;
const metersBetween = (a, b) => {
  const R = 6371000;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
};

const getLatLng = (v) => {
  if (v?.lat != null && v?.lng != null) return { lat: +v.lat, lng: +v.lng };
  if (v?.latitude != null && v?.longitude != null)
    return { lat: +v.latitude, lng: +v.longitude };
  if (v?.location?.lat != null && v?.location?.lng != null)
    return { lat: +v.location.lat, lng: +v.location.lng };
  if (v?.geometry?.location) {
    const g = v.geometry.location;
    const lat = typeof g.lat === 'function' ? g.lat() : g.lat;
    const lng = typeof g.lng === 'function' ? g.lng() : g.lng;
    if (lat != null && lng != null) return { lat: +lat, lng: +lng };
  }
  return null;
};

function isClosedVenueForDisplay(v) {
  const statusText = String(
    v?.openStatus ||
    v?.open_status ||
    v?.business_status ||
    v?.status ||
    ""
  ).toLowerCase();

  return (
    v?.opening_hours?.open_now === false ||
    v?.current_opening_hours?.open_now === false ||
    v?.open_now === false ||
    v?.openNow === false ||
    v?.isOpen === false ||
    statusText === "closed" ||
    statusText.includes("closed now") ||
    statusText.includes("closed_temporarily") ||
    statusText.includes("closed_permanently") ||
    v?.business_status === "CLOSED_TEMPORARILY" ||
    v?.business_status === "CLOSED_PERMANENTLY"
  );
}

function cleanVenuesForDisplay(items) {
  if (!Array.isArray(items)) return [];

  const cleaned = items.filter((v) => !isClosedVenueForDisplay(v));

  if (items.length !== cleaned.length) {
    console.log("[FRONTEND CLOSED VENUE GUARD]", {
      before: items.length,
      after: cleaned.length,
      dropped: items
        .filter((v) => isClosedVenueForDisplay(v))
        .map((v) => ({
          name: v.name,
          open_now: v.open_now,
          openNow: v.openNow,
          isOpen: v.isOpen,
          openStatus: v.openStatus,
          business_status: v.business_status,
        })),
    });
  }

  return cleaned;
}

// --- Fresh location helper ---
async function getFreshLocation() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation not supported'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        resolve({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
        }),
      (err) => reject(err),
                                             { enableHighAccuracy: true, timeout: 8000, maximumAge: 15000 }
    );
  });
}
// ---- Anonymous per-device id (no login) ----
function isSponsoredVenue(venue) {
  return Boolean(
    venue?.isSponsored ||
    venue?.isFeatured ||
    (Array.isArray(venue?.tags) &&
      venue.tags.some((tag) => /^(sponsored|featured)$/i.test(String(tag || '').trim())))
  );
}

function createSearchSessionId() {
  try {
    const browserCrypto =
      typeof window !== 'undefined'
        ? window.crypto
        : null;

    const raw = browserCrypto?.randomUUID?.();
    if (raw) return `search_${raw}`;
  } catch {}

  return `search_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
}

function getOrCreatePartyUid() {
  const KEY = 'PARTY_UID_V1';
  try {
    const existing = localStorage.getItem(KEY);
    if (existing && existing.length >= 12) return existing;

    const uid =
      (crypto?.randomUUID?.() ||
        `pb_${Math.random().toString(36).slice(2)}_${Date.now()}`);

    localStorage.setItem(KEY, uid);
    return uid;
  } catch {
    return `pb_${Math.random().toString(36).slice(2)}_${Date.now()}`;
  }
}

function getAutoTimeCategory() {
  const hour = new Date().getHours();

  if (hour >= 6 && hour < 10) return 'coffee';
  if (hour >= 10 && hour < 13) return 'brunch';
  if (hour >= 13 && hour < 16) return 'food';
  if (hour >= 16 && hour < 22) return 'bars';
  if (hour >= 22 || hour < 2) return 'clubs';
  return 'late_night';
}


// BACKEND_AUTHORITATIVE_SEARCH_PRESENTATION_V1
function normalizeBackendSearchCategory(search = {}, firstResult = null) {
  const raw = String(
    search?.displayCategory ||
      search?.effectiveCategory ||
      search?.effectiveType ||
      firstResult?.searchDisplayCategory ||
      firstResult?.searchEffectiveType ||
      firstResult?.type ||
      ''
  )
    .trim()
    .toLowerCase();

  return normalizeCategoryKey(raw) || 'food';
}

function backendSearchModeLabel(search = {}, category = '') {
  return String(search?.displayModeLabel || '').trim() ||
    getCategoryModeLabel(category);
}

function backendSearchDisplayLabel(search = {}, query = '', firstResult = null) {
  return String(
    search?.displayLabel ||
      firstResult?.searchDisplayLabel ||
      firstResult?.searchLabel ||
      `🔎 ${query}`
  ).trim();
}

function getLoadingMessages(category) {
  switch (category) {
    case 'coffee':
      return [
        'Brewing nearby energy…',
        'Finding morning coffee…',
        'Scanning nearby cafés…',
      ];

    case 'brunch':
      return [
        'Finding brunch spots…',
        'Scanning nearby patios…',
        'Looking for mimosas…',
      ];

    case 'bars':
      return [
        'Finding happy hour…',
        'Scanning nearby bars…',
        'Looking for strong pours…',
      ];

    case 'clubs':
      return [
        'Finding nightlife…',
        'Scanning dance floors…',
        'Looking for tonight’s vibe…',
      ];

    default:
      return [
        'Finding nearby energy…',
        'Scanning the city…',
        'Looking for good spots…',
      ];
  }
}

const REVIEW_STATS_KEY = 'PB_REVIEW_STATS_V1';

function todayKey() {
  return new Date().toISOString().slice(0, 10);
}

function getReviewStats() {
  try {
    return JSON.parse(localStorage.getItem(REVIEW_STATS_KEY)) || {
      presses: 0,
      days: [],
      shown: false,
    };
  } catch {
    return { presses: 0, days: [], shown: false };
  }
}

async function maybeAskForReview() {
  if (!isNative) return;

  const stats = getReviewStats();
  const today = todayKey();

  const days = Array.from(new Set([...(stats.days || []), today]));
  const presses = Number(stats.presses || 0) + 1;

  const next = {
    presses,
    days,
    shown: !!stats.shown,
  };

  localStorage.setItem(REVIEW_STATS_KEY, JSON.stringify(next));

  if (!next.shown && presses >= 5 && days.length >= 2) {
    localStorage.setItem(
      REVIEW_STATS_KEY,
      JSON.stringify({ ...next, shown: true })
    );

    try {
      await InAppReview.requestReview();
    } catch (e) {
      console.warn('Review prompt failed:', e);
    }
  }
}


const ELECTION_2026_RATINGS = [
  { rating: 5, emoji: '🔥', label: 'Very favorable' },
  { rating: 4, emoji: '😍', label: 'Favorable' },
  { rating: 3, emoji: '🙂', label: 'Neutral' },
  { rating: 2, emoji: '😐', label: 'Unfavorable' },
  { rating: 1, emoji: '💩', label: 'Very unfavorable' },
];

const ELECTION_CANDIDATE_SOURCE_URL =
  'https://www.sf.gov/candidates-november-3-2026-general-election';

const ELECTION_CANDIDATE_SOURCE_DATE =
  'August 5, 2026';

const ELECTION_EMOJI_STYLE = {
  fontFamily:
    '"Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif',
  fontStyle: 'normal',
  fontWeight: 400,
};

function Election2026Sheet({
  partyUid,
  onClose,
  onOpenUrl,
}) {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [savingCandidateId, setSavingCandidateId] =
    useState('');
  const viewedCandidateIds = useRef(new Set());
  const electionSheetRef = useRef(null);

  const loadCandidates = async () => {
    setLoading(true);
    setLoadError('');

    try {
      const data = await fetchJSON(
        '/api/election-2026/candidates',
        {
          headers: {
            'X-Party-Uid': partyUid,
          },
        }
      );

      setCandidates(
        Array.isArray(data?.candidates)
          ? data.candidates
          : []
      );
    } catch (error) {
      console.warn(
        'Election 2026 load failed:',
        error
      );

      setLoadError(
        'The candidate lineup could not be loaded.'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCandidates();
  }, []);

  useEffect(() => {
    const runEmojiRescue = () => {
      if (electionSheetRef.current) {
        rescueEmoji(electionSheetRef.current);
      }
    };

    runEmojiRescue();

    const frame = requestAnimationFrame(
      runEmojiRescue
    );
    const timer = setTimeout(
      runEmojiRescue,
      120
    );

    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(timer);
    };
  }, [loading, candidates]);

  const recordCandidateView = (candidateId) => {
    if (
      !candidateId ||
      viewedCandidateIds.current.has(candidateId)
    ) {
      return;
    }

    viewedCandidateIds.current.add(candidateId);

    fetchJSON(
      `/api/election-2026/candidates/${encodeURIComponent(
        candidateId
      )}/view`,
      {
        method: 'POST',
      }
    ).catch((error) => {
      console.warn(
        'Candidate view event failed:',
        error
      );
    });
  };

  const submitRating = async (
    candidateId,
    rating
  ) => {
    if (!candidateId || savingCandidateId) return;

    setSavingCandidateId(candidateId);

    try {
      const data = await fetchJSON(
        `/api/election-2026/candidates/${encodeURIComponent(
          candidateId
        )}/rating`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Party-Uid': partyUid,
          },
          body: JSON.stringify({ rating }),
        }
      );

      if (data?.candidate) {
        setCandidates((current) =>
          current.map((candidate) =>
            candidate.id === candidateId
              ? data.candidate
              : candidate
          )
        );
      }
    } catch (error) {
      console.warn(
        'Candidate rating failed:',
        error
      );

      alert(
        'Your rating could not be saved. Please try again.'
      );
    } finally {
      setSavingCandidateId('');
    }
  };

  const openLink = async (url) => {
    if (!url) return;

    if (onOpenUrl) {
      await onOpenUrl(url);
      return;
    }

    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const instagramUrl = (instagram) => {
    const value = String(instagram || '').trim();

    if (!value) return '';

    if (/^https?:\/\//i.test(value)) return value;

    return `https://www.instagram.com/${value.replace(
      /^@/,
      ''
    )}/`;
  };

  return (
    <div
      ref={electionSheetRef}
      role="dialog"
      aria-modal="true"
      aria-label="Candidate Vibe Poll Election 2026"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000,
        background:
          'linear-gradient(180deg, #111827 0%, #030712 100%)',
        color: '#fff',
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      <div
        style={{
          width: 'min(100%, 720px)',
          margin: '0 auto',
          padding:
            'calc(18px + env(safe-area-inset-top)) 16px calc(40px + env(safe-area-inset-bottom))',
          boxSizing: 'border-box',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 16,
            marginBottom: 18,
          }}
        >
          <div>
            <div
              style={{
                color: '#fbbf24',
                fontSize: 13,
                fontWeight: 800,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                marginBottom: 5,
              }}
            >
              Election 2026
            </div>

            <h1
              style={{
                fontSize: 28,
                lineHeight: 1.05,
                margin: 0,
              }}
            >
              Candidate Vibe Poll
            </h1>

          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label="Close Election 2026"
            style={{
              width: 42,
              height: 42,
              flex: '0 0 42px',
              borderRadius: '50%',
              border: '1px solid rgba(255,255,255,.22)',
              background: 'rgba(255,255,255,.1)',
              color: '#fff',
              fontSize: 25,
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>

        <div
          style={{
            padding: '12px 14px',
            marginBottom: 18,
            borderRadius: 14,
            background: 'rgba(251,191,36,.12)',
            border: '1px solid rgba(251,191,36,.3)',
            color: '#fde68a',
            fontSize: 14,
            lineHeight: 1.4,
          }}
        >
          <strong>
            Unofficial community-opinion poll.
          </strong>{' '}
          This is not an election, endorsement, or official
          voting service. Ratings reflect aggregated
          political sentiment—not a candidate’s appearance
          or personal worth.

          <div style={{ marginTop: 8 }}>
            One rating per device, per candidate. You can
            change your rating anytime. Community sentiment
            is the rounded average of all current ratings.
            Individual ratings and totals are never shown.
          </div>
        </div>

        {loading && (
          <div
            style={{
              padding: 30,
              textAlign: 'center',
              color: '#cbd5e1',
            }}
          >
            Loading the candidate lineup…
          </div>
        )}

        {!loading && loadError && (
          <div
            style={{
              padding: 24,
              textAlign: 'center',
              borderRadius: 16,
              background: 'rgba(255,255,255,.07)',
            }}
          >
            <p>{loadError}</p>

            <button
              type="button"
              onClick={loadCandidates}
              style={{
                border: 0,
                borderRadius: 999,
                padding: '11px 18px',
                background: '#fbbf24',
                color: '#111827',
                fontWeight: 800,
              }}
            >
              Try Again
            </button>
          </div>
        )}

        {!loading &&
          !loadError &&
          candidates.length === 0 && (
            <div
              style={{
                padding: 28,
                textAlign: 'center',
                borderRadius: 18,
                background: 'rgba(255,255,255,.07)',
                border:
                  '1px solid rgba(255,255,255,.12)',
              }}
            >
              <div
                style={{
                  fontSize: 38,
                  marginBottom: 8,
                }}
              >
                <span aria-hidden="true" style={ELECTION_EMOJI_STYLE}>🗳️</span>
              </div>

              <strong>Candidate lineup coming soon</strong>

              <p
                style={{
                  color: '#cbd5e1',
                  marginBottom: 0,
                }}
              >
                Candidates will appear here after their
                information has been verified.
              </p>
            </div>
          )}

        <div
          style={{
            display: 'grid',
            gap: 15,
          }}
        >
          {candidates.map((candidate) => {
            const isSaving =
              savingCandidateId === candidate.id;

            return (
              <article
                key={candidate.id}
                onMouseEnter={() =>
                  recordCandidateView(candidate.id)
                }
                onTouchStart={() =>
                  recordCandidateView(candidate.id)
                }
                style={{
                  borderRadius: 20,
                  overflow: 'hidden',
                  background: 'rgba(255,255,255,.08)',
                  border:
                    '1px solid rgba(255,255,255,.13)',
                  boxShadow:
                    '0 15px 30px rgba(0,0,0,.18)',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    gap: 14,
                    padding: 16,
                    alignItems: 'center',
                  }}
                >
                  {candidate.photo ? (
                    <img
                      src={candidate.photo}
                      alt=""
                      onError={(event) => {
                        event.currentTarget.style.display =
                          'none';
                      }}
                      style={{
                        width: 76,
                        height: 76,
                        flex: '0 0 76px',
                        objectFit: 'cover',
                        borderRadius: 16,
                        background: '#1f2937',
                      }}
                    />
                  ) : (
                    <div
                      aria-hidden="true"
                      style={{
                        width: 76,
                        height: 76,
                        flex: '0 0 76px',
                        display: 'grid',
                        placeItems: 'center',
                        borderRadius: 16,
                        background:
                          'linear-gradient(135deg, #7c3aed, #db2777)',
                        fontSize: 34,
                      }}
                    >
                      <span aria-hidden="true" style={ELECTION_EMOJI_STYLE}>🗳️</span>
                    </div>
                  )}

                  <div
                    style={{
                      minWidth: 0,
                      flex: 1,
                    }}
                  >
                    <div
                      style={{
                        color: '#fbbf24',
                        fontSize: 12,
                        fontWeight: 800,
                        textTransform: 'uppercase',
                        letterSpacing: '0.06em',
                      }}
                    >
                      District {candidate.district}
                    </div>

                    <h2
                      style={{
                        margin: '3px 0 8px',
                        fontSize: 21,
                        lineHeight: 1.15,
                      }}
                    >
                      {candidate.name}
                    </h2>

                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 10,
                      }}
                    >
                      {candidate.campaignWebsite && (
                        <button
                          type="button"
                          onClick={() =>
                            openLink(
                              candidate.campaignWebsite
                            )
                          }
                          style={{
                            padding: 0,
                            border: 0,
                            background: 'transparent',
                            color: '#93c5fd',
                            fontWeight: 700,
                          }}
                        >
                          Website
                        </button>
                      )}

                      {candidate.instagram && (
                        <button
                          type="button"
                          onClick={() =>
                            openLink(
                              instagramUrl(
                                candidate.instagram
                              )
                            )
                          }
                          style={{
                            padding: 0,
                            border: 0,
                            background: 'transparent',
                            color: '#f9a8d4',
                            fontWeight: 700,
                          }}
                        >
                          Instagram
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    padding: '0 16px 13px',
                  }}
                >
                  <div
                    style={{
                      padding: '10px 12px',
                      textAlign: 'center',
                      borderRadius: 12,
                      background: 'rgba(0,0,0,.2)',
                      color: candidate.vibeLocked
                        ? '#cbd5e1'
                        : '#fff',
                      fontWeight: 800,
                    }}
                  >
                    {candidate.vibeLocked ? (
                      candidate.lockedMessage
                    ) : (
                      <>
                        Community sentiment:{' '}
                        <span aria-hidden="true" style={ELECTION_EMOJI_STYLE}>{candidate.currentVibe?.emoji}</span>{' '}
                        {candidate.currentVibe?.label}
                      </>
                    )}
                  </div>
                </div>


                <div
                  role="group"
                  aria-label={`Share your opinion about ${candidate.name}`}
                  style={{
                    display: 'grid',
                    gridTemplateColumns:
                      'repeat(5, minmax(0, 1fr))',
                    gap: 5,
                    padding: '0 10px 15px',
                  }}
                >
                  {ELECTION_2026_RATINGS.map(
                    (option) => {
                      const selected =
                        candidate.myRating ===
                        option.rating;

                      return (
                        <button
                          key={option.rating}
                          type="button"
                          disabled={isSaving}
                          onClick={() => {
                            recordCandidateView(
                              candidate.id
                            );

                            submitRating(
                              candidate.id,
                              option.rating
                            );
                          }}
                          aria-pressed={selected}
                          aria-label={option.label}
                          style={{
                            minWidth: 0,
                            minHeight: 72,
                            padding: '7px 2px',
                            borderRadius: 12,
                            border: selected
                              ? '2px solid #fbbf24'
                              : '1px solid rgba(255,255,255,.15)',
                            background: selected
                              ? 'rgba(251,191,36,.18)'
                              : 'rgba(255,255,255,.06)',
                            color: '#fff',
                            opacity: isSaving ? 0.6 : 1,
                          }}
                        >
                          <span
                            aria-hidden="true"
                            style={{
                              display: 'block',
                              fontSize: 25,
                              lineHeight: 1.15,
                            }}
                          >
                            <span aria-hidden="true" style={ELECTION_EMOJI_STYLE}>{option.emoji}</span>
                          </span>

                          <span
                            style={{
                              display: 'block',
                              marginTop: 5,
                              fontSize: 9,
                              lineHeight: 1.1,
                              fontWeight: 800,
                            }}
                          >
                            {option.label}
                          </span>
                        </button>
                      );
                    }
                  )}
                </div>
              </article>
            );
          })}
        </div>

        {!loading &&
          !loadError &&
          candidates.length > 0 && (
            <div
              style={{
                marginTop: 22,
                padding: 16,
                textAlign: 'center',
                borderRadius: 16,
                background: 'rgba(124,58,237,.16)',
                border:
                  '1px solid rgba(167,139,250,.25)',
                color: '#ddd6fe',
                fontSize: 13,
                lineHeight: 1.45,
              }}
            >
              <p style={{ margin: '0 0 9px' }}>
                Candidate Vibe Poll is not affiliated with
                the City and County of San Francisco, the
                Department of Elections, Apple, or any
                candidate or campaign. Ratings do not
                constitute votes in an election.
              </p>

              <p style={{ margin: '0 0 9px' }}>
                Candidate names and districts are sourced
                from the San Francisco Department of
                Elections. Campaign links are provided for
                reference. Information last reviewed{' '}
                {ELECTION_CANDIDATE_SOURCE_DATE}.
              </p>

              <button
                type="button"
                onClick={() =>
                  openLink(
                    ELECTION_CANDIDATE_SOURCE_URL
                  )
                }
                style={{
                  padding: 0,
                  border: 0,
                  background: 'transparent',
                  color: '#c4b5fd',
                  fontWeight: 800,
                  textDecoration: 'underline',
                }}
              >
                Official election information
              </button>
            </div>
          )}
      </div>
    </div>
  );
}


function HappeningTonightSheet({
  onClose,
  onOpenUrl,
  onShowEventOnMap,
  onEventSelect,
  onEventExternalOpen,
  isEventSaved,
  onToggleSavedEvent,
  deviceLocation,
}) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [selectedNeighborhood, setSelectedNeighborhood] =
    useState('');
  const [dateMode, setDateMode] = useState('today');
  const [categoryFilter, setCategoryFilter] =
    useState('all');

  const sheetRef = useRef(null);

  const loadEvents = async (mode = dateMode) => {
    setLoading(true);
    setLoadError('');

    try {
      let path = '/api/events?limit=200';

      const now = new Date();

      const startOfDay = (date) => {
        const value = new Date(date);
        value.setHours(0, 0, 0, 0);
        return value;
      };

      const addDays = (date, days) => {
        const value = new Date(date);
        value.setDate(value.getDate() + days);
        return value;
      };

      if (mode === 'today') {
        path += '&today=true';
      } else if (mode === 'tomorrow') {
        const start = addDays(startOfDay(now), 1);
        const end = addDays(start, 1);

        path +=
          `&from=${encodeURIComponent(start.toISOString())}` +
          `&to=${encodeURIComponent(end.toISOString())}`;
      } else if (mode === 'weekend') {
        const today = startOfDay(now);
        const day = today.getDay();

        const daysUntilFriday =
          day <= 5 ? 5 - day : 6;

        const start =
          day === 6
            ? today
            : addDays(today, daysUntilFriday);

        const end = addDays(start, day === 6 ? 2 : 3);

        path +=
          `&from=${encodeURIComponent(start.toISOString())}` +
          `&to=${encodeURIComponent(end.toISOString())}`;
      } else {
        const end = new Date(
          now.getTime() +
          7 * 24 * 60 * 60 * 1000
        );

        path +=
          `&from=${encodeURIComponent(now.toISOString())}` +
          `&to=${encodeURIComponent(end.toISOString())}`;
      }

      const data = await fetchJSON(path, {
        cache: 'no-store',
      });

      setEvents(
        Array.isArray(data?.events)
          ? data.events
          : []
      );
    } catch (error) {
      console.warn(
        'Happening Tonight load failed:',
        error
      );

      setLoadError(
        'Events could not be loaded right now.'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEvents(dateMode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateMode]);

  useEffect(() => {
    const runEmojiRescue = () => {
      if (sheetRef.current) {
        rescueEmoji(sheetRef.current);
      }
    };

    runEmojiRescue();

    const frame =
      requestAnimationFrame(runEmojiRescue);

    const timer =
      setTimeout(runEmojiRescue, 120);

    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(timer);
    };
  }, [
    loading,
    events,
    selectedNeighborhood,
    dateMode,
    categoryFilter,
  ]);

  const formatEventTime = (value) => {
    if (!value) return '';

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) return '';

    return new Intl.DateTimeFormat(
      'en-US',
      {
        timeZone: 'America/Los_Angeles',
        hour: 'numeric',
        minute: '2-digit',
      }
    ).format(date);
  };

  const formatEventDate = (value) => {
    if (!value) return '';

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) return '';

    return new Intl.DateTimeFormat(
      'en-US',
      {
        timeZone: 'America/Los_Angeles',
        weekday: 'short',
        month: 'short',
        day: 'numeric',
      }
    ).format(date);
  };

  const categoryEmoji = (category) => {
    switch (
      String(category || '').toLowerCase()
    ) {
      case 'music':
        return '🎵';
      case 'comedy':
        return '😂';
      case 'arts':
        return '🎨';
      case 'sports':
        return '🏟️';
      case 'family':
        return '🎈';
      case 'festival':
        return '🎉';
      default:
        return '🔥';
    }
  };

  const filteredEvents =
    categoryFilter === 'all'
      ? events
      : events.filter(
          (event) =>
            String(event?.category || '')
              .toLowerCase() === categoryFilter
        );

  const eventDistanceMeters = (event) => {
    const userLat = Number(
      deviceLocation?.latitude
    );

    const userLng = Number(
      deviceLocation?.longitude
    );

    const eventLat = Number(
      event?.coordinates?.latitude
    );

    const eventLng = Number(
      event?.coordinates?.longitude
    );

    if (
      !Number.isFinite(userLat) ||
      !Number.isFinite(userLng) ||
      !Number.isFinite(eventLat) ||
      !Number.isFinite(eventLng)
    ) {
      return Infinity;
    }

    return metersBetween(
      {
        lat: userLat,
        lng: userLng,
      },
      {
        lat: eventLat,
        lng: eventLng,
      }
    );
  };

  const formatEventDistance = (event) => {
    const meters = eventDistanceMeters(event);

    if (!Number.isFinite(meters)) return '';

    const miles = meters / 1609.344;

    if (miles < 0.1) {
      return `${Math.max(
        1,
        Math.round(meters * 3.28084)
      )} ft away`;
    }

    return `${
      miles < 10
        ? miles.toFixed(1)
        : Math.round(miles)
    } mi away`;
  };

  const neighborhoodGroups = filteredEvents.reduce(
    (groups, event) => {
      const neighborhood =
        String(
          event?.neighborhood ||
          'Neighborhood unavailable'
        ).trim();

      if (!groups[neighborhood]) {
        groups[neighborhood] = [];
      }

      groups[neighborhood].push(event);

      return groups;
    },
    {}
  );

  const neighborhoods = Object.entries(
    neighborhoodGroups
  )
    .map(([name, items]) => ({
      name,
      events: items.sort(
        (a, b) =>
          new Date(a.startDateTime) -
          new Date(b.startDateTime)
      ),
    }))
    .sort(
      (a, b) =>
        b.events.length - a.events.length ||
        a.name.localeCompare(b.name)
    );

  const nearbyEvents = [...filteredEvents]
    .filter(
      (event) =>
        Number.isFinite(eventDistanceMeters(event))
    )
    .sort((a, b) => {
      const distanceDifference =
        eventDistanceMeters(a) -
        eventDistanceMeters(b);

      if (distanceDifference !== 0) {
        return distanceDifference;
      }

      return (
        new Date(a.startDateTime) -
        new Date(b.startDateTime)
      );
    })
    .slice(0, 40);

  const selectedEvents =
    selectedNeighborhood === '__nearby__'
      ? nearbyEvents
      : selectedNeighborhood
      ? neighborhoodGroups[selectedNeighborhood] || []
      : [];

  const openEvent = async (event) => {
    const url = String(event?.eventUrl || '').trim();

    if (!url) return;

    onEventSelect?.(event);
    onEventExternalOpen?.(event);

    if (typeof onOpenUrl === 'function') {
      await onOpenUrl(url);
      return;
    }

    window.open(
      url,
      '_blank',
      'noopener,noreferrer'
    );
  };

  return (
    <div
      ref={sheetRef}
      role="dialog"
      aria-modal="true"
      aria-label="Happening Tonight"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000,
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
        color: '#fff',
        background:
          'linear-gradient(180deg, #1f102f 0%, #09020f 100%)',
      }}
    >
      <div
        style={{
          width: 'min(100%, 720px)',
          minHeight: '100%',
          margin: '0 auto',
          padding:
            'calc(18px + env(safe-area-inset-top)) 16px calc(40px + env(safe-area-inset-bottom))',
          boxSizing: 'border-box',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 16,
            marginBottom: 18,
          }}
        >
          <div>
            <div
              style={{
                color: '#fb7185',
                fontSize: 13,
                fontWeight: 900,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                marginBottom: 5,
              }}
            >
              The Party Button
            </div>

            <h1
              style={{
                margin: 0,
                fontSize: 29,
                lineHeight: 1.05,
              }}
            >
              🔥 Happening Tonight
            </h1>

            <p
              style={{
                margin: '8px 0 0',
                color: '#ddd6fe',
                lineHeight: 1.4,
              }}
            >
              See what is happening across San Francisco
              neighborhoods.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label="Close Happening Tonight"
            style={{
              width: 42,
              height: 42,
              flex: '0 0 42px',
              borderRadius: '50%',
              border:
                '1px solid rgba(255,255,255,.22)',
              background:
                'rgba(255,255,255,.1)',
              color: '#fff',
              fontSize: 25,
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns:
              'repeat(2, minmax(0, 1fr))',
            gap: 8,
            marginBottom: 12,
            padding: 5,
            borderRadius: 16,
            background:
              'rgba(255,255,255,.08)',
          }}
        >
          {[
            ['today', 'Tonight'],
            ['tomorrow', 'Tomorrow'],
            ['weekend', 'This Weekend'],
            ['week', 'Next 7 Days'],
          ].map(([mode, label]) => (
            <button
              key={mode}
              type="button"
              onClick={() => {
                setSelectedNeighborhood('');
                setDateMode(mode);
              }}
              style={{
                minHeight: 42,
                border: 0,
                borderRadius: 12,
                background:
                  dateMode === mode
                    ? 'linear-gradient(90deg, #ec4899, #8b5cf6)'
                    : 'transparent',
                color: '#fff',
                fontWeight: 900,
              }}
            >
              {label}
            </button>
          ))}
        </div>

        <div
          style={{
            display: 'flex',
            gap: 7,
            overflowX: 'auto',
            paddingBottom: 8,
            marginBottom: 10,
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {[
            ['all', 'All'],
            ['music', 'Music'],
            ['comedy', 'Comedy'],
            ['arts', 'Arts'],
            ['sports', 'Sports'],
            ['festival', 'Festivals'],
            ['family', 'Family'],
            ['other', 'Other'],
          ].map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => {
                setCategoryFilter(key);
                setSelectedNeighborhood('');
              }}
              style={{
                flex: '0 0 auto',
                minHeight: 35,
                padding: '0 12px',
                borderRadius: 999,
                border:
                  categoryFilter === key
                    ? '1px solid #f9a8d4'
                    : '1px solid rgba(255,255,255,.14)',
                background:
                  categoryFilter === key
                    ? 'rgba(236,72,153,.24)'
                    : 'rgba(255,255,255,.07)',
                color: '#fff',
                fontWeight: 900,
                fontSize: 12,
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {selectedNeighborhood && (
          <button
            type="button"
            onClick={() =>
              setSelectedNeighborhood('')
            }
            style={{
              marginBottom: 14,
              padding: 0,
              border: 0,
              background: 'transparent',
              color: '#f9a8d4',
              fontWeight: 900,
              fontSize: 15,
            }}
          >
            ← All neighborhoods
          </button>
        )}

        {loading && (
          <div
            style={{
              padding: 40,
              textAlign: 'center',
              color: '#ddd6fe',
            }}
          >
            Scanning the city…
          </div>
        )}

        {!loading && loadError && (
          <div
            style={{
              padding: 24,
              textAlign: 'center',
              borderRadius: 18,
              background:
                'rgba(255,255,255,.07)',
              border:
                '1px solid rgba(255,255,255,.12)',
            }}
          >
            <p>{loadError}</p>

            <button
              type="button"
              onClick={() => loadEvents(dateMode)}
              style={{
                border: 0,
                borderRadius: 999,
                padding: '11px 18px',
                background: '#ec4899',
                color: '#fff',
                fontWeight: 900,
              }}
            >
              Try Again
            </button>
          </div>
        )}

        {!loading &&
          !loadError &&
          events.length === 0 && (
            <div
              style={{
                padding: 30,
                textAlign: 'center',
                borderRadius: 18,
                background:
                  'rgba(255,255,255,.07)',
                border:
                  '1px solid rgba(255,255,255,.12)',
              }}
            >
              <div
                style={{
                  fontSize: 38,
                  marginBottom: 8,
                }}
              >
                🌙
              </div>

              <strong>
                Nothing listed right now
              </strong>

              <p
                style={{
                  color: '#ddd6fe',
                  marginBottom: 0,
                }}
              >
                Check the next seven days or come back
                later.
              </p>
            </div>
          )}

        {!loading &&
          !loadError &&
          !selectedNeighborhood && (
            <div
              style={{
                display: 'grid',
                gap: 10,
              }}
            >
              {nearbyEvents.length > 0 && (
                <button
                  type="button"
                  onClick={() =>
                    setSelectedNeighborhood(
                      '__nearby__'
                    )
                  }
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent:
                      'space-between',
                    gap: 14,
                    padding: '16px 17px',
                    borderRadius: 18,
                    border:
                      '1px solid rgba(45,212,191,.32)',
                    background:
                      'linear-gradient(135deg, rgba(20,184,166,.25), rgba(59,130,246,.18))',
                    color: '#fff',
                    textAlign: 'left',
                    boxShadow:
                      '0 10px 24px rgba(0,0,0,.16)',
                  }}
                >
                  <span
                    style={{
                      minWidth: 0,
                      fontSize: 18,
                      fontWeight: 900,
                    }}
                  >
                    📍 Happening Near You
                  </span>

                  <span
                    style={{
                      flex: '0 0 auto',
                      padding: '6px 10px',
                      borderRadius: 999,
                      background:
                        'rgba(255,255,255,.12)',
                      color: '#ccfbf1',
                      fontSize: 13,
                      fontWeight: 900,
                    }}
                  >
                    {nearbyEvents.length}
                  </span>
                </button>
              )}

              {neighborhoods.map(
                ({ name, events: items }) => (
                  <button
                    key={name}
                    type="button"
                    onClick={() =>
                      setSelectedNeighborhood(name)
                    }
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent:
                        'space-between',
                      gap: 14,
                      padding: '16px 17px',
                      borderRadius: 18,
                      border:
                        '1px solid rgba(255,255,255,.13)',
                      background:
                        'linear-gradient(135deg, rgba(236,72,153,.22), rgba(139,92,246,.16))',
                      color: '#fff',
                      textAlign: 'left',
                      boxShadow:
                        '0 10px 24px rgba(0,0,0,.16)',
                    }}
                  >
                    <span
                      style={{
                        minWidth: 0,
                        fontSize: 18,
                        fontWeight: 900,
                      }}
                    >
                      {name}
                    </span>

                    <span
                      style={{
                        flex: '0 0 auto',
                        padding: '6px 10px',
                        borderRadius: 999,
                        background:
                          'rgba(255,255,255,.12)',
                        color: '#fce7f3',
                        fontSize: 13,
                        fontWeight: 900,
                      }}
                    >
                      {items.length}{' '}
                      {items.length === 1
                        ? 'event'
                        : 'events'}
                    </span>
                  </button>
                )
              )}
            </div>
          )}

        {!loading &&
          !loadError &&
          selectedNeighborhood && (
            <>
              <div
                style={{
                  marginBottom: 14,
                }}
              >
                <h2
                  style={{
                    margin: 0,
                    fontSize: 25,
                  }}
                >
                  {selectedNeighborhood ===
                  '__nearby__'
                    ? '📍 Happening Near You'
                    : selectedNeighborhood}
                </h2>

                <div
                  style={{
                    marginTop: 4,
                    color: '#ddd6fe',
                    fontWeight: 700,
                  }}
                >
                  {selectedEvents.length}{' '}
                  {selectedEvents.length === 1
                    ? 'event'
                    : 'events'}
                </div>
              </div>

              <div
                style={{
                  display: 'grid',
                  gap: 13,
                }}
              >
                {[...selectedEvents]
                  .sort(
                    (a, b) =>
                      new Date(a.startDateTime) -
                      new Date(b.startDateTime)
                  )
                  .map((event) => (
                    <article
                      key={
                        event.id ||
                        event.externalId ||
                        `${event.name}-${event.startDateTime}`
                      }
                      style={{
                        overflow: 'hidden',
                        borderRadius: 20,
                        background:
                          'rgba(255,255,255,.08)',
                        border:
                          '1px solid rgba(255,255,255,.13)',
                        boxShadow:
                          '0 15px 30px rgba(0,0,0,.18)',
                      }}
                    >
                      {event.imageUrl && (
                        <img
                          src={event.imageUrl}
                          alt=""
                          onError={(e) => {
                            e.currentTarget.style.display =
                              'none';
                          }}
                          style={{
                            display: 'block',
                            width: '100%',
                            height: 170,
                            objectFit: 'cover',
                            background: '#16081e',
                          }}
                        />
                      )}

                      <div
                        style={{
                          padding: 16,
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            justifyContent:
                              'space-between',
                            alignItems:
                              'flex-start',
                            gap: 12,
                          }}
                        >
                          <div
                            style={{
                              minWidth: 0,
                            }}
                          >
                            <div
                              style={{
                                color: '#f9a8d4',
                                fontSize: 13,
                                fontWeight: 900,
                                textTransform:
                                  'uppercase',
                                letterSpacing:
                                  '0.04em',
                              }}
                            >
                              {formatEventDate(
                                event.startDateTime
                              )}{' '}
                              ·{' '}
                              {formatEventTime(
                                event.startDateTime
                              )}
                            </div>

                            <h3
                              style={{
                                margin:
                                  '5px 0 7px',
                                fontSize: 21,
                                lineHeight: 1.15,
                              }}
                            >
                              {event.name}
                            </h3>
                          </div>

                          <div
                            aria-hidden="true"
                            style={{
                              flex: '0 0 auto',
                              fontSize: 27,
                            }}
                          >
                            {categoryEmoji(
                              event.category
                            )}
                          </div>
                        </div>

                        {event.venueName && (
                          <div
                            style={{
                              color: '#fff',
                              fontWeight: 800,
                              lineHeight: 1.35,
                            }}
                          >
                            {event.venueName}
                          </div>
                        )}

                        {event.address && (
                          <div
                            style={{
                              marginTop: 3,
                              color: '#c4b5fd',
                              fontSize: 13,
                              lineHeight: 1.35,
                            }}
                          >
                            {event.address}
                          </div>
                        )}

                        {selectedNeighborhood ===
                          '__nearby__' &&
                          formatEventDistance(event) && (
                            <div
                              style={{
                                marginTop: 5,
                                color: '#99f6e4',
                                fontSize: 13,
                                fontWeight: 900,
                              }}
                            >
                              📍 {formatEventDistance(event)}
                            </div>
                          )}

                        {event.subcategory && (
                          <div
                            style={{
                              display: 'inline-block',
                              marginTop: 10,
                              padding: '5px 9px',
                              borderRadius: 999,
                              background:
                                'rgba(139,92,246,.22)',
                              color: '#ddd6fe',
                              fontSize: 12,
                              fontWeight: 900,
                              textTransform:
                                'capitalize',
                            }}
                          >
                            {event.subcategory}
                          </div>
                        )}

                        <button
                          type="button"
                          onClick={() =>
                            onToggleSavedEvent?.(event)
                          }
                          style={{
                            width: '100%',
                            minHeight: 40,
                            marginTop: 12,
                            borderRadius: 13,
                            border:
                              '1px solid rgba(249,168,212,.32)',
                            background:
                              isEventSaved?.(event)
                                ? 'rgba(236,72,153,.24)'
                                : 'rgba(255,255,255,.08)',
                            color: '#fff',
                            fontWeight: 900,
                            fontSize: 13,
                          }}
                        >
                          {isEventSaved?.(event)
                            ? '♥ Saved Event'
                            : '♡ Save Event'}
                        </button>

                        <div
                          style={{
                            display: 'grid',
                            gridTemplateColumns:
                              event.eventUrl
                                ? 'repeat(2, minmax(0, 1fr))'
                                : '1fr',
                            gap: 9,
                            marginTop: 14,
                          }}
                        >
                          <button
                            type="button"
                            onClick={() => {
                              onEventSelect?.(event);
                              onShowEventOnMap?.(event);
                            }}
                            disabled={
                              !Number.isFinite(
                                Number(
                                  event?.coordinates?.latitude
                                )
                              ) ||
                              !Number.isFinite(
                                Number(
                                  event?.coordinates?.longitude
                                )
                              )
                            }
                            style={{
                              minHeight: 44,
                              border: 0,
                              borderRadius: 14,
                              background:
                                'rgba(255,255,255,.14)',
                              color: '#fff',
                              fontWeight: 900,
                              fontSize: 14,
                              opacity:
                                Number.isFinite(
                                  Number(
                                    event?.coordinates?.latitude
                                  )
                                ) &&
                                Number.isFinite(
                                  Number(
                                    event?.coordinates?.longitude
                                  )
                                )
                                  ? 1
                                  : 0.45,
                            }}
                          >
                            📍 Show on Map
                          </button>

                          {event.eventUrl && (
                            <button
                              type="button"
                              onClick={() =>
                                openEvent(event)
                              }
                              style={{
                                minHeight: 44,
                                border: 0,
                                borderRadius: 14,
                                background:
                                  'linear-gradient(90deg, #ec4899, #8b5cf6)',
                                color: '#fff',
                                fontWeight: 900,
                                fontSize: 14,
                              }}
                            >
                              Event Details
                            </button>
                          )}
                        </div>
                      </div>
                    </article>
                  ))}
              </div>
            </>
          )}

        {!loading &&
          !loadError &&
          events.length > 0 && (
            <div
              style={{
                marginTop: 22,
                color: '#a78bfa',
                textAlign: 'center',
                fontSize: 12,
                lineHeight: 1.4,
              }}
            >
              Event details may change. Confirm times and
              ticket availability with the event organizer.
            </div>
          )}
      </div>
    </div>
  );
}


function SavedSheet({
  savedVenues = [],
  savedEvents = [],
  onClose,
  onOpenVenue,
  onOpenEventMap,
  onOpenEventDetails,
  onRemoveVenue,
  onRemoveEvent,
}) {
  const formatSavedEventDate = (value) => {
    if (!value) return '';

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return '';
    }

    return new Intl.DateTimeFormat(
      'en-US',
      {
        timeZone: 'America/Los_Angeles',
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      }
    ).format(date);
  };

  const empty =
    savedVenues.length === 0 &&
    savedEvents.length === 0;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Saved spots and events"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000,
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
        color: '#fff',
        background:
          'linear-gradient(180deg, #24142d 0%, #09040d 100%)',
      }}
    >
      <div
        style={{
          width: 'min(100%, 720px)',
          minHeight: '100%',
          margin: '0 auto',
          padding:
            'calc(18px + env(safe-area-inset-top)) 16px calc(40px + env(safe-area-inset-bottom))',
          boxSizing: 'border-box',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 16,
            marginBottom: 20,
          }}
        >
          <div>
            <div
              style={{
                color: '#f9a8d4',
                fontSize: 13,
                fontWeight: 900,
                letterSpacing: '.08em',
                textTransform: 'uppercase',
              }}
            >
              The Party Button
            </div>

            <h1
              style={{
                margin: '4px 0 0',
                fontSize: 29,
                lineHeight: 1.05,
              }}
            >
              ♥ Saved
            </h1>
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label="Close Saved"
            style={{
              width: 42,
              height: 42,
              flex: '0 0 42px',
              borderRadius: '50%',
              border:
                '1px solid rgba(255,255,255,.22)',
              background:
                'rgba(255,255,255,.1)',
              color: '#fff',
              fontSize: 25,
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>

        {empty ? (
          <div
            style={{
              padding: '38px 22px',
              textAlign: 'center',
              borderRadius: 20,
              background:
                'rgba(255,255,255,.07)',
              border:
                '1px solid rgba(255,255,255,.12)',
            }}
          >
            <div style={{ fontSize: 40 }}>
              ♡
            </div>

            <strong
              style={{
                display: 'block',
                marginTop: 8,
              }}
            >
              Nothing saved yet
            </strong>

            <div
              style={{
                marginTop: 6,
                color: '#d8c9de',
                fontSize: 14,
                lineHeight: 1.4,
              }}
            >
              Tap a heart on a venue or Save Event
              to keep it here.
            </div>
          </div>
        ) : null}

        {savedVenues.length > 0 ? (
          <section style={{ marginTop: 10 }}>
            <h2
              style={{
                fontSize: 18,
                marginBottom: 10,
              }}
            >
              📍 Saved Spots
            </h2>

            <div
              style={{
                display: 'grid',
                gap: 9,
              }}
            >
              {savedVenues.map((venue) => (
                <div
                  key={savedVenueId(venue)}
                  style={{
                    padding: 13,
                    borderRadius: 16,
                    background:
                      'rgba(255,255,255,.08)',
                    border:
                      '1px solid rgba(255,255,255,.11)',
                  }}
                >
                  <strong>
                    {venue?.name || 'Saved spot'}
                  </strong>

                  <div
                    style={{
                      marginTop: 4,
                      color: '#d8c9de',
                      fontSize: 12,
                    }}
                  >
                    {venue?.neighborhood ||
                      venue?.vicinity ||
                      venue?.formatted_address ||
                      'Location unavailable'}
                  </div>

                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns:
                        '1fr auto',
                      gap: 8,
                      marginTop: 10,
                    }}
                  >
                    <button
                      type="button"
                      onClick={() =>
                        onOpenVenue?.(venue)
                      }
                      style={{
                        minHeight: 40,
                        border: 0,
                        borderRadius: 12,
                        background:
                          'linear-gradient(90deg, #ec4899, #8b5cf6)',
                        color: '#fff',
                        fontWeight: 900,
                      }}
                    >
                      📍 Open Map
                    </button>

                    <button
                      type="button"
                      onClick={() =>
                        onRemoveVenue?.(venue)
                      }
                      aria-label={`Remove ${
                        venue?.name || 'venue'
                      } from Saved`}
                      style={{
                        minWidth: 42,
                        borderRadius: 12,
                        border:
                          '1px solid rgba(255,255,255,.18)',
                        background:
                          'rgba(255,255,255,.08)',
                        color: '#fff',
                        fontWeight: 900,
                      }}
                    >
                      ×
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        ) : null}

        {savedEvents.length > 0 ? (
          <section style={{ marginTop: 24 }}>
            <h2
              style={{
                fontSize: 18,
                marginBottom: 10,
              }}
            >
              🔥 Saved Events
            </h2>

            <div
              style={{
                display: 'grid',
                gap: 9,
              }}
            >
              {savedEvents.map((event) => (
                <div
                  key={savedEventId(event)}
                  style={{
                    padding: 13,
                    borderRadius: 16,
                    background:
                      'rgba(255,255,255,.08)',
                    border:
                      '1px solid rgba(255,255,255,.11)',
                  }}
                >
                  <strong>
                    {event?.name || 'Saved event'}
                  </strong>

                  <div
                    style={{
                      marginTop: 4,
                      color: '#f9a8d4',
                      fontSize: 12,
                      fontWeight: 800,
                    }}
                  >
                    {formatSavedEventDate(
                      event?.startDateTime
                    )}
                  </div>

                  <div
                    style={{
                      marginTop: 3,
                      color: '#d8c9de',
                      fontSize: 12,
                    }}
                  >
                    {[
                      event?.venueName,
                      event?.neighborhood,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </div>

                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns:
                        event?.eventUrl
                          ? '1fr 1fr auto'
                          : '1fr auto',
                      gap: 8,
                      marginTop: 10,
                    }}
                  >
                    <button
                      type="button"
                      onClick={() =>
                        onOpenEventMap?.(event)
                      }
                      style={{
                        minHeight: 40,
                        border: 0,
                        borderRadius: 12,
                        background:
                          'rgba(255,255,255,.14)',
                        color: '#fff',
                        fontWeight: 900,
                      }}
                    >
                      📍 Map
                    </button>

                    {event?.eventUrl ? (
                      <button
                        type="button"
                        onClick={() =>
                          onOpenEventDetails?.(
                            event
                          )
                        }
                        style={{
                          minHeight: 40,
                          border: 0,
                          borderRadius: 12,
                          background:
                            'linear-gradient(90deg, #ec4899, #8b5cf6)',
                          color: '#fff',
                          fontWeight: 900,
                        }}
                      >
                        Details
                      </button>
                    ) : null}

                    <button
                      type="button"
                      onClick={() =>
                        onRemoveEvent?.(event)
                      }
                      aria-label={`Remove ${
                        event?.name || 'event'
                      } from Saved`}
                      style={{
                        minWidth: 42,
                        borderRadius: 12,
                        border:
                          '1px solid rgba(255,255,255,.18)',
                        background:
                          'rgba(255,255,255,.08)',
                        color: '#fff',
                        fontWeight: 900,
                      }}
                    >
                      ×
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        ) : null}
      </div>
    </div>
  );
}


function App() {
    const handleShareApp = async () => {
      const shareData = {
        title: 'The Party Button',
        text: 'Find the vibe before you arrive.',
        url: APP_STORE_URL,
      };

      try {
        if (navigator.share) {
          await navigator.share(shareData);
        } else {
          await navigator.clipboard.writeText(APP_STORE_URL);
          alert('App Store link copied!');
        }
      } catch (e) {
        console.warn('Share failed:', e);
      }
    };
    
    
    const handleWildCard = () => {
      if (!venues.length) {
        alert('No venues loaded yet.');
        return;
      }

      const randomVenue =
        venues[Math.floor(Math.random() * venues.length)];

      setSelectedVenue(randomVenue);
      saveLastVenue(randomVenue);

      setMapDetailMode('venue');
      setAnchorVenue(randomVenue);
        setMapVenues(venues);

      setView('map');
    };
    const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
    const [view, setView] = useState('slideshow');
    const [deviceLocation, setDeviceLocation] = useState({
        latitude: null,
        longitude: null,
    });
    const [outsideSupportedMarket, setOutsideSupportedMarket] = useState(false);

    const [originLocation, setOriginLocation] = useState({
        latitude: null,
        longitude: null,
    });

    const [activeMarketId, setActiveMarketId] = useState(() => {
      try {
        const saved = localStorage.getItem('PB_ACTIVE_MARKET');
        if (getMarketById(saved)) return saved;
      } catch {}
      return DEFAULT_MARKET_ID;
    });

    const activeMarket =
      getMarketById(activeMarketId) ||
      DEFAULT_MARKET;

    // PB_OAKLAND_FEATURE_GATES_V1: each market opts in to data-backed extras.
    const marketFeatures = activeMarket?.features || {};

    const setActiveMarketSafe = (marketId) => {
      const next = getMarketById(marketId);
      if (!next) return;

      setActiveMarketId(next.id);
      try {
        localStorage.setItem('PB_ACTIVE_MARKET', next.id);
      } catch {}
    };

    // Follow a changed origin, but do not undo an explicit city search merely
    // because the active market changed while the phone stayed in another city.
    useEffect(() => {
      const market = getMarketForCoordinates(
        originLocation?.latitude,
        originLocation?.longitude
      );
      if (!market) return;

      setActiveMarketId((current) => current === market.id ? current : market.id);
      try {
        localStorage.setItem('PB_ACTIVE_MARKET', market.id);
      } catch {}
    }, [originLocation?.latitude, originLocation?.longitude]);
    const [venues, setVenues] = useState([]);

    
    const [activeCategory, setActiveCategory] = useState(() => {
      try {
        const saved = localStorage.getItem('PB_ACTIVE_CATEGORY');
        if (saved) return saved;
      } catch {}
      return 'coffee';
    });
    
    // Auto vs Manual category selection (prevents time-of-day from hijacking user choice)
    const [categoryMode, setCategoryMode] = useState(() => {
       try { return localStorage.getItem('PB_CATEGORY_MODE') || 'auto'; } catch { return 'auto'; }
     });
     const categoryModeRef = useRef(categoryMode);
     useEffect(() => { categoryModeRef.current = categoryMode; }, [categoryMode]);
    
    const setCategoryModeSafe = (mode) => {
      categoryModeRef.current = mode;
      setCategoryMode(mode);
      try { localStorage.setItem('PB_CATEGORY_MODE', mode); } catch {}
    };
    
     const setActiveCategorySafe = (cat) => {
       setActiveCategory(cat);
       try { localStorage.setItem('PB_ACTIVE_CATEGORY', cat); } catch {}
     };
    
    const [artistPool, setArtistPool] = useState([]);

    const combinedFallbackPool = [
      ...(artistPool || []),
      ...DEFAULT_PHOTO_POOL,
    ].filter(isSafeImage);

    const artistOnlyPool = (artistPool || []).filter(isSafeImage);
    const [mapDetailMode, setMapDetailMode] = useState('venue');
    const [exploreOpen, setExploreOpen] = useState(false);
    const [eventsOpen, setEventsOpen] = useState(false);
    const [partyQuestOpen, setPartyQuestOpen] = useState(false);

    // PB_PARTY_QUEST_MAIN_ENTRY_V1
    const [partyQuestPromo, setPartyQuestPromo] =
      useState(null);

    const [savedOpen, setSavedOpen] =
      useState(false);

    const [savedVenues, setSavedVenues] =
      useState(() =>
        readSavedItems(SAVED_VENUES_KEY)
      );

    const [savedEvents, setSavedEvents] =
      useState(() =>
        readSavedItems(SAVED_EVENTS_KEY)
      );

    const [electionOpen, setElectionOpen] = useState(false);

    useEffect(() => {
      if (!marketFeatures.partyQuest) {
        setPartyQuestOpen(false);
        setPartyQuestPromo(null);
      }
      if (!marketFeatures.events) setEventsOpen(false);
      if (!marketFeatures.election) setElectionOpen(false);
    }, [marketFeatures.partyQuest, marketFeatures.events, marketFeatures.election]);
    const [mapboxApiKey, setMapboxApiKey] = useState('');
    const [selectedVenue, setSelectedVenue] = useState(null);
    const [partyMode, setPartyMode] = useState(false);
    const [mapVenues, setMapVenues] = useState([]);
    const [anchorVenue, setAnchorVenue] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    // PB_NEIGHBORHOOD_BROWSE_V1
    const [selectedNeighborhood, setSelectedNeighborhood] = useState('');
    const neighborhoodHintRef = useRef('');
    const venueId = (v) => v?.place_id || v?.placeId || v?.id || v?.name || null;
    const [partyUid] = useState(() => getOrCreatePartyUid());

    const trackAnalytics = (eventType, payload = {}) => {
      if (!eventType || isWebPromoOnly) return;

      fetchJSON('/api/analytics/event', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Party-Uid': partyUid,
        },
        body: JSON.stringify({
          eventType,
          source: isNative ? 'ios' : 'web',
          marketId: activeMarket?.id || DEFAULT_MARKET_ID,
          marketName: activeMarket?.name || '',
          ...payload,
        }),
      }).catch((error) => {
        // Analytics should never interrupt the Party Button experience.
        console.warn(
          '[Analytics] event failed:',
          eventType,
          error?.message || error
        );
      });
    };

    const analyticsVenuePayload = (venue) => {
      const attribution = venue?.searchAttribution || venue || {};

      return {
        placeId:
          venue?.place_id ||
          venue?.placeId ||
          venue?.id ||
          '',
        venueName: venue?.name || '',
        category:
          venue?.type ||
          venue?.category ||
          attribution?.searchEffectiveType ||
          activeCategoryRef.current ||
          '',
        neighborhood:
          venue?.searchNeighborhood ||
          venue?.neighborhood ||
          attribution?.searchNeighborhood ||
          neighborhoodHintRef.current ||
          '',
        query: attribution?.searchQuery || '',
        searchLabel: venue?.searchLabel || searchContextLabel || '',
        resultCount: attribution?.searchResultCount ?? null,
        searchTraceId: attribution?.searchTraceId || '',
        searchSessionId: attribution?.searchSessionId || '',
        resultRank: attribution?.resultRank ?? null,
        matchScore: attribution?.matchScore ?? null,
        matchConfidence: attribution?.matchConfidence || '',
        gateTier: attribution?.gateTier || '',
        broadeningMode: attribution?.broadeningMode || '',
        retrievalMode: attribution?.retrievalMode || '',
        retrievalSource: attribution?.retrievalSource || '',
        searchElapsedMs: attribution?.searchElapsedMs ?? null,
        isClosestResult: attribution?.isClosestResult === true,
        isHighestRatedResult: attribution?.isHighestRatedResult === true,
      };
    };

    const analyticsEventPayload = (event = {}) => ({
      eventId:
        event?.id ||
        event?.externalId ||
        '',
      eventName: event?.name || '',
      eventSource: event?.source || '',
      venueName: event?.venueName || '',
      category: event?.category || '',
      neighborhood: event?.neighborhood || '',
    });

    const isVenueSaved = (venue) => {
      const id = savedVenueId(venue);

      return Boolean(
        id &&
        savedVenues.some(
          (item) =>
            savedVenueId(item) === id
        )
      );
    };

    const isEventSaved = (event) => {
      const id = savedEventId(event);

      return Boolean(
        id &&
        savedEvents.some(
          (item) =>
            savedEventId(item) === id
        )
      );
    };

    const toggleSavedVenue = (venue) => {
      const id = savedVenueId(venue);

      if (!id) return;

      const removing =
        isVenueSaved(venue);

      const next = removing
        ? savedVenues.filter(
            (item) =>
              savedVenueId(item) !== id
          )
        : [venue, ...savedVenues]
            .slice(0, 100);

      setSavedVenues(next);

      writeSavedItems(
        SAVED_VENUES_KEY,
        next
      );

      trackAnalytics(
        removing
          ? 'favorite_remove'
          : 'favorite_add',
        analyticsVenuePayload(venue)
      );
    };

    const toggleSavedEvent = (event) => {
      const id = savedEventId(event);

      if (!id) return;

      const removing =
        isEventSaved(event);

      const next = removing
        ? savedEvents.filter(
            (item) =>
              savedEventId(item) !== id
          )
        : [event, ...savedEvents]
            .slice(0, 100);

      setSavedEvents(next);

      writeSavedItems(
        SAVED_EVENTS_KEY,
        next
      );

      trackAnalytics(
        removing
          ? 'favorite_remove'
          : 'favorite_add',
        analyticsEventPayload(event)
      );
    };

    const openEvents = () => {
      if (!marketFeatures.events) return;
      trackAnalytics('events_open');
      setEventsOpen(true);
    };


    // PB_PARTY_QUEST_MAIN_ENTRY_V1
    // Show one compact Party Quest entry only while the backend
    // has at least one active quest. Catalog order remains the
    // promotion priority, except an in-progress quest always wins.
    useEffect(() => {
      if (
        isWebPromoOnly ||
        !marketFeatures.partyQuest ||
        view !== 'categories' ||
        partyQuestOpen
      ) {
        return undefined;
      }

      let cancelled = false;

      fetchJSON(
        '/api/quests',
        {
          headers: {
            'X-Party-Uid':
              partyUid,
          },

          cache:
            'no-store',
        }
      )
        .then((data) => {
          if (cancelled) return;

          const list =
            Array.isArray(
              data?.quests
            )
              ? data.quests
              : [];

          const inProgress =
            list.find(
              (item) =>
                item?.progress?.status ===
                'active'
            );

          const notCompleted =
            list.find(
              (item) =>
                item?.progress?.status !==
                'completed'
            );

          setPartyQuestPromo(
            inProgress ||
            notCompleted ||
            list[0] ||
            null
          );
        })
        .catch((error) => {
          if (cancelled) return;

          console.warn(
            '[Party Quest] promo load failed:',
            error?.message || error
          );

          setPartyQuestPromo(null);
        });

      return () => {
        cancelled = true;
      };
    }, [
      partyQuestOpen,
      partyUid,
      view,
      marketFeatures.partyQuest,
    ]);


    const handleEventSelect = (event) => {
      trackAnalytics(
        'event_select',
        analyticsEventPayload(event)
      );
    };

    const handleEventExternalOpen = (event) => {
      trackAnalytics(
        'event_external_open',
        analyticsEventPayload(event)
      );
    };

    useEffect(() => {
      // Record the first app session immediately.
      // The server collapses repeated opens within 30 minutes.
      trackAnalytics('app_open');

      const recordVisibleSession = () => {
        if (document.visibilityState === 'visible') {
          trackAnalytics('app_open');
        }
      };

      document.addEventListener(
        'visibilitychange',
        recordVisibleSession
      );

      return () => {
        document.removeEventListener(
          'visibilitychange',
          recordVisibleSession
        );
      };

      // Analytics sender and party UID remain stable for this app mount.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleFeaturedImpression = (venue) => {
      if (!isSponsoredVenue(venue)) return;
      trackAnalytics('featured_impression', analyticsVenuePayload(venue));
    };

    const handleClaimSubmitted = (venue) => {
      if (!venue) return;

      trackAnalytics('claim_submit', analyticsVenuePayload(venue));

      if (isSponsoredVenue(venue)) {
        trackAnalytics('featured_claim', analyticsVenuePayload(venue));
      }
    };

    const searchAbortRef = useRef(null);
    const venueFetchSeqRef = useRef(0);
    const [isSearchingNeighborhood, setIsSearchingNeighborhood] = useState(false);
    const [locationIntro, setLocationIntro] = useState(false);
    const [searchContextLabel, setSearchContextLabel] = useState('✨ Best Nearby Right Now');
    const [searchModeCategory, setSearchModeCategory] = useState(null);
    const [searchModeLabel, setSearchModeLabel] = useState('');
    const [searchScopeLabel, setSearchScopeLabel] = useState('');

    useEffect(() => {
      const run = () => {
        rescueEmoji(document.querySelector('.App') || document.body);
      };

      run();

      const raf = requestAnimationFrame(run);
      const timer = setTimeout(run, 120);

      return () => {
        cancelAnimationFrame(raf);
        clearTimeout(timer);
      };
    }, [
      view,
      venues,
      selectedVenue,
      activeCategory,
      categoryMode,
      searchContextLabel,
      locationIntro,
      exploreOpen,
      outsideSupportedMarket,
      isSearchingNeighborhood,
    ]);
    
    useEffect(() => {
      const loadingCategory =
        searchModeCategory ||
        (categoryMode === 'auto'
          ? getAutoTimeCategory()
          : activeCategory);

      const msgs = getLoadingMessages(loadingCategory);

      setLoadingMessageIndex(0);

      const interval = setInterval(() => {
        setLoadingMessageIndex((i) => (i + 1) % msgs.length);
      }, 2200);

      return () => clearInterval(interval);
    }, [activeCategory, categoryMode, searchModeCategory]);

    // Track the "current" category without relying on re-mounts (iOS often resumes instead)
    const activeCategoryRef = useRef(activeCategory);
    useEffect(() => {
      activeCategoryRef.current = activeCategory;
    }, [activeCategory]);
    const originRef = useRef(originLocation);
    useEffect(() => { originRef.current = originLocation; }, [originLocation]);

    const originPinnedRef = useRef(false);


    const focusReqIdRef = useRef(0);
    // ---------------------------
    // Scroll position persistence (Categories list -> Map -> Back to List)
    // ---------------------------
    const listScrollRef = useRef(null);
    // Venue fetch guardrails:
    // - abort in-flight venue loads when user taps around quickly
    // - light throttle (prevents accidental double-taps & menu spam)
    // - short client-side cooldown if we get a 429 (rate limited)
    const venuesAbortRef = useRef(null);
    const venuesLastFetchAtRef = useRef(0);
    const venuesCooldownUntilRef = useRef(0);
    const listScrollKey = () => `PB_SCROLL_V1:${activeCategory || 'all'}`;

    // Track recent scrolling so we don't "fight" the user
    const lastUserScrollRef = useRef(0);
    const scrollSaveRafRef = useRef(null);

    const saveListScrollNow = () => {
      try {
        const y = listScrollRef.current?.scrollTop ?? 0;
        sessionStorage.setItem(listScrollKey(), String(y));
        lastUserScrollRef.current = Date.now();
      } catch {}
    };
    
    // Back-compat alias (you call saveListScroll() elsewhere)
    const saveListScroll = saveListScrollNow;

    // Throttled scroll handler (runs at most once per animation frame)
    const handleListScroll = () => {
        const el = listScrollRef.current;
          if (el && el.scrollLeft !== 0) {
            el.scrollLeft = 0;
            requestAnimationFrame(() => { if (el.scrollLeft !== 0) el.scrollLeft = 0; });
          }
        
        lastUserScrollRef.current = Date.now();
      if (scrollSaveRafRef.current) return;

      scrollSaveRafRef.current = requestAnimationFrame(() => {
        scrollSaveRafRef.current = null;
        saveListScrollNow();
      });
    };

    const restoreListScroll = () => {
      let y = 0;
      try {
        y = Number(sessionStorage.getItem(listScrollKey()) || 0);
      } catch {}

      // Wait for DOM paint (and list render) before restoring scrollTop
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (listScrollRef.current) {
              listScrollRef.current.scrollLeft = 0;
              listScrollRef.current.scrollTop = y;
          }
        });
      });
    };

    const featuredArtist = {
      title: '✨ Become a Featured Artist',
      subtitle:
        'We’re looking for photographers, nightlife artists & creatives to take over the app with your photos.',
      links: [
        {
          label: 'Apply for Takeover',
          url: 'mailto:support@the-party-button.com?subject=Photo%20Takeover%20Application',
        },
      ],
    };

    const openExternal = async (url) => {
      if (!url) return false;

      const s = String(url).trim();
      const lower = s.toLowerCase();

      // OS-native schemes should leave the web view immediately.
      if (
        lower.startsWith('mailto:') ||
        lower.startsWith('tel:') ||
        lower.startsWith('sms:') ||
        lower.startsWith('maps://') ||
        lower.startsWith('comgooglemaps://')
      ) {
        window.location.href = s;
        return true;
      }

      try {
        if (isNative) {
          await Browser.open({ url: s });
          return true;
        }
      } catch (err) {
        console.warn('Capacitor Browser failed, falling back:', err);
      }

      const opened = window.open(s, "_blank", "noopener,noreferrer");
      return opened !== null;
    };

    const handleMapOpenUrl = async (url, venue = null) => {
      const normalizedUrl = String(url || '').toLowerCase();
      const targetVenue = venue || selectedVenue || anchorVenue;

      const looksLikeDirections =
        normalizedUrl.includes('google.com/maps') ||
        normalizedUrl.includes('maps.apple.com') ||
        normalizedUrl.startsWith('maps://') ||
        normalizedUrl.startsWith('comgooglemaps://') ||
        normalizedUrl.includes('dir_action=navigate') ||
        normalizedUrl.includes('/dir/') ||
        normalizedUrl.includes('destination=') ||
        normalizedUrl.includes('daddr=');

      const opened = await openExternal(url);

      // Count a route start only after handing the user to external navigation.
      if (opened && looksLikeDirections && targetVenue) {
        trackAnalytics(
          'route_start',
          analyticsVenuePayload(targetVenue)
        );

        // PB_POTTY_ANALYTICS_V1: record a Bathroom handoff after Maps opens.
        if (
          targetVenue.isRestroom || targetVenue.restroom ||
          targetVenue.type === 'restroom' || targetVenue.category === 'restroom'
        ) {
          trackAnalytics('potty_route_start', {
            ...analyticsVenuePayload(targetVenue),
            category: 'restroom',
          });
        }

        if (isSponsoredVenue(targetVenue)) {
          trackAnalytics(
            'featured_route',
            analyticsVenuePayload(targetVenue)
          );
        }
      }

      return opened;
    };

    const handleRouteView = (venue) => {
      if (!venue) return;
      trackAnalytics(
        'route_view',
        analyticsVenuePayload(venue)
      );
    };



    const frameStyle = usingHttpOrigin
        ? {
            position: 'relative',
            width: '100%',
            maxWidth: '100%',
            minHeight: '100dvh',
            overflowY: 'auto',
            overflowX: 'hidden',
            WebkitOverflowScrolling: 'touch',
        }
        : {
            position: 'fixed',
            inset: 0,
            width: '100%',
            maxWidth: '100%',
            height: '100dvh',
            overflow: 'hidden',
            overflowX: 'hidden',
        };
    const handlePottyButton = async () => {
      if (!marketFeatures.restrooms) return;
      if (isWebPromoOnly) {
        window.location.href = APP_STORE_URL;
        return;
      }

      // PB_POTTY_ANALYTICS_V1: a press is counted even if location/search fails.
      trackAnalytics('potty_button_press', { category: 'restroom' });

      const requestId = ++venueFetchSeqRef.current;
      setIsSearchingNeighborhood(true);
      neighborhoodHintRef.current = '';
      setSelectedNeighborhood('');

      try {
        try { venuesAbortRef.current?.abort?.(); } catch {}
        const ac = new AbortController();
        venuesAbortRef.current = ac;

        let origin = null;
        try {
          origin = await getFreshLocation();
          writeLastLocation(origin);
          setDeviceLocation(origin);
          setOriginLocation(origin);
          originRef.current = origin;
          originPinnedRef.current = false;
        } catch {
          origin =
            readLastLocation() ||
            (Number.isFinite(deviceLocation?.latitude) && Number.isFinite(deviceLocation?.longitude)
              ? deviceLocation
              : null) ||
            (Number.isFinite(originLocation?.latitude) && Number.isFinite(originLocation?.longitude)
              ? originLocation
              : null);
        }

        if (!origin) {
          trackAnalytics('potty_search_error', { category: 'restroom', success: false });
          alert('Your location is needed to find nearby restrooms.');
          return;
        }

        const data = await fetchJSON('/api/restrooms', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Party-Uid': partyUid,
          },
          body: JSON.stringify({
            latitude: origin.latitude,
            longitude: origin.longitude,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles',
          }),
          signal: ac.signal,
        });

        if (requestId !== venueFetchSeqRef.current || ac.signal.aborted) return;

        const restroomResults = Array.isArray(data)
          ? data
          : Array.isArray(data?.restrooms)
          ? data.restrooms
          : Array.isArray(data?.venues)
          ? data.venues
          : [];

        const cleaned = restroomResults
          .filter((item) => getLatLng(item))
          .map((item) => ({
            ...item,
            type: 'restroom',
            category: 'restroom',
            isRestroom: true,
            emoji: item.emoji || '🚻',
          }));

        setSelectedVenue(null);
        setAnchorVenue(null);
        setVenues(cleaned);
        setMapVenues(cleaned);
        setMapDetailMode('restroom');
        setSearchContextLabel('🚻 Restrooms Near You');
        setSearchModeCategory(null);
        setSearchModeLabel('Potty Button');
        setSearchScopeLabel('Near your location');
        setView('map');

        trackAnalytics('potty_search', {
          category: 'restroom',
          resultCount: cleaned.length,
          success: cleaned.length > 0,
        });

        if (!cleaned.length) {
          alert('No nearby restroom locations were found. Try again from another area.');
        }
      } catch (error) {
        if (error?.name === 'AbortError' || requestId !== venueFetchSeqRef.current) return;
        trackAnalytics('potty_search_error', { category: 'restroom', success: false });
        console.warn('Potty Button failed:', error);
        alert('Failed to load nearby restrooms.');
      } finally {
        if (requestId === venueFetchSeqRef.current) setIsSearchingNeighborhood(false);
      }
    };


    const fetchVenues = async (
      category,
      locOverride,
      pinnedOverride = null,
      modeOverride = categoryModeRef.current,
      marketOverrideId = null
    ) => {
      const requestMarket = getMarketById(marketOverrideId) || activeMarket;
      if (isWebPromoOnly) {
        window.location.href = APP_STORE_URL;
        return;
      }

      const requestId = ++venueFetchSeqRef.current;
      setIsSearchingNeighborhood(true);

      const now = Date.now();

      if (now < venuesCooldownUntilRef.current) {
        const waitS = Math.ceil((venuesCooldownUntilRef.current - now) / 1000);
        console.log('[Venues] cooldown active, skipping fetch for', waitS, 's');
        if (requestId === venueFetchSeqRef.current) {
          setIsSearchingNeighborhood(false);
        }
        return;
      }

      if (now - (venuesLastFetchAtRef.current || 0) < 500) {
        console.log('[Venues] throttled (too soon)');
        if (requestId === venueFetchSeqRef.current) {
          setIsSearchingNeighborhood(false);
        }
        return;
      }

      venuesLastFetchAtRef.current = now;

      try { venuesAbortRef.current?.abort?.(); } catch {}
      const ac = new AbortController();
      venuesAbortRef.current = ac;

      let origin =
        locOverride ||
        (originLocation &&
        typeof originLocation.latitude === 'number' &&
        typeof originLocation.longitude === 'number'
          ? originLocation
          : null) ||
        (deviceLocation &&
        typeof deviceLocation.latitude === 'number' &&
        typeof deviceLocation.longitude === 'number'
          ? deviceLocation
          : null);

        if (!origin) {
          const last = readLastLocation();
          if (last) {
            origin = last;
          } else {
            if (requestId === venueFetchSeqRef.current) {
              setIsSearchingNeighborhood(false);
            }
            return;
          }
        }

      try {
        let data;

          const neighborhoodHint = String(neighborhoodHintRef.current || '').trim();

          const neighborhoodPayload = neighborhoodHint
            ? {
                query: neighborhoodHint,
                neighborhood: neighborhoodHint,
                neighborhoodName: neighborhoodHint,
                isNeighborhoodSearch: true,
              }
            : {};
          
        if (modeOverride === 'auto' && !category) {
          data = await fetchJSON('/api/suggested-venues', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Party-Uid': partyUid,
            },
              body: JSON.stringify({
                latitude: origin.latitude,
                longitude: origin.longitude,
                marketId: requestMarket?.id || DEFAULT_MARKET_ID,
                marketName: requestMarket?.name || '',
                ...neighborhoodPayload,
                timezone:
                  Intl.DateTimeFormat().resolvedOptions().timeZone ||
                  'America/Los_Angeles',
              }),
            signal: ac.signal,
          });
        } else {
            const endpoint = getCategoryEndpoint(
              category || activeCategoryRef.current
            );
            if (!endpoint) throw new Error('Unknown category');

            data = await fetchJSON(endpoint, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X-Party-Uid': partyUid,
              },
                body: JSON.stringify({
                  latitude: origin.latitude,
                  longitude: origin.longitude,
                  marketId: requestMarket?.id || DEFAULT_MARKET_ID,
                  marketName: requestMarket?.name || '',
                  ...neighborhoodPayload,
                  timezone:
                    Intl.DateTimeFormat().resolvedOptions().timeZone ||
                    'America/Los_Angeles',
                }),
              signal: ac.signal,
            });
            }

            const deduped = Array.isArray(data)
          ? data
          : Array.isArray(data?.venues)
          ? data.venues
          : [];

          const effectivePinned =
            pinnedOverride &&
            !pinnedOverride?.isNeighborhood &&
            pinnedOverride?.type !== "neighborhood"
              ? pinnedOverride
              : null;

          const pinnedId = venueId(effectivePinned);

        const shouldKeepPinned = (() => {
          if (!effectivePinned) return false;
          const pLL = getLatLng(effectivePinned);
          const oLL = origin ? { lat: origin.latitude, lng: origin.longitude } : null;
          if (!pLL || !oLL) return false;
          return metersBetween(pLL, oLL) < 1200;
        })();

          const cleanedDeduped = cleanVenuesForDisplay(deduped);

          const withPinned =
            shouldKeepPinned && !isClosedVenueForDisplay(effectivePinned)
              ? [
                  effectivePinned,
                  ...cleanedDeduped.filter(
                    (v) => !v?.pinned && venueId(v) !== pinnedId
                  ),
                ]
              : cleanedDeduped.filter((v) => !v?.pinned);

          if (requestId !== venueFetchSeqRef.current || ac.signal.aborted) return;
          
          setVenues(withPinned);
          setMapVenues([]);
          setAnchorVenue(null);
          return withPinned;
      } catch (e) {
        if (e?.name === 'AbortError') return;

        if (requestId !== venueFetchSeqRef.current) return;

        if (e?.status === 429) {
          const ra = Number(e?.retryAfter) || 15;
          venuesCooldownUntilRef.current = Date.now() + ra * 1000;
          alert(`Searches are temporarily rate-limited. Try again in ${ra}s.`);
          return;
        }

        console.warn('fetchVenues error:', e);
        alert(`Failed to load venues${e?.status ? ` (${e.status})` : ''}.`);
      } finally {
        if (requestId === venueFetchSeqRef.current) {
          setIsSearchingNeighborhood(false);
        }
      }
    };

    const browseSupportedMarket = (marketId) => {
      const market = getMarketById(marketId);
      if (!market) return;
      const center = {
        latitude: Number(market.center.latitude),
        longitude: Number(market.center.longitude),
      };
      originPinnedRef.current = true;
      setOutsideSupportedMarket(false);
      setActiveMarketSafe(market.id);
      setOriginLocation(center);
      setDeviceLocation(center);
      writeLastLocation(center);
      setSelectedVenue(null);
      setAnchorVenue(null);
      setMapVenues([]);
      fetchVenues(activeCategory, center, null, categoryModeRef.current, market.id);
    };

    useEffect(() => {
      if (view === 'categories') restoreListScroll();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [view, activeCategory]);

    
    useEffect(() => {
      let cancelled = false;

      const loadArtistFallbacks = async () => {
        try {
          const timezone =
            Intl.DateTimeFormat().resolvedOptions().timeZone ||
            'America/Los_Angeles';
          const data = await fetchJSON(
            `/api/fallbacks?timezone=${encodeURIComponent(timezone)}`,
            { cache: 'no-store' }
          );

          const images = (Array.isArray(data?.creatives) ? data.creatives : [])
            .map((creative) =>
              typeof creative === 'string'
                ? creative
                : creative?.imageUrl || creative?.url || creative?.src || ''
            )
            .map((value) => String(value || '').trim())
            .filter(isSafeImage);

          if (!cancelled) setArtistPool(images);
        } catch (error) {
          console.warn(
            '[Fallbacks] artist pool unavailable:',
            error?.message || error
          );
          if (!cancelled) setArtistPool([]);
        }
      };

      loadArtistFallbacks();
      return () => {
        cancelled = true;
      };
    }, []);
    
   
   

    
    useEffect(() => {
      if (isWebPromoOnly) return;

      const fallback = { latitude: 37.7749, longitude: -122.4194 };

      const applyLocOnly = (loc) => {
        setDeviceLocation(loc);
        setOriginLocation((prev) =>
          typeof prev.latitude === 'number' && typeof prev.longitude === 'number'
            ? prev
            : loc
        );
        setOutsideSupportedMarket(!isWithinSupportedMarket(loc.latitude, loc.longitude));
      };

        const seedLocation = (loc) => {
          applyLocOnly(loc);
        };

        const last = readLastLocation();
        const seed = last || fallback;
        seedLocation(seed);

      (async () => {
        const envKey = process.env.REACT_APP_MAPBOX_API_KEY || '';
        const urls = [
          api('/get-api-key?api=mapbox'),
          api('/api/get-api-key?api=mapbox'),
        ];

        for (const url of urls) {
          try {
            const r = await fetch(url, { cache: 'no-store' });
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            const d = await r.json();
            const k = (d && (d.apiKey || d.key || d.token)) || '';
            if (k) {
              setMapboxApiKey(k);
              return;
            }
          } catch {}
        }

        if (envKey) setMapboxApiKey(envKey);
      })();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleCategoryClick = (cat) => {
      const neighborhood = String(neighborhoodHintRef.current || '').trim();

      setCategoryModeSafe('manual');
      setActiveCategorySafe(cat);
      activeCategoryRef.current = cat;

      setSearchQuery('');
      setSearchModeCategory(cat);
      setSearchModeLabel(getCategoryModeLabel(cat));
      setSearchScopeLabel(neighborhood);
      setSearchContextLabel(
        neighborhood
          ? `📍 ${neighborhood} · ${getCategoryModeLabel(cat)}`
          : getCategorySearchLabel(cat)
      );

      // Keep the neighborhood filter when the user explicitly picks a category.
      // An explicit category beats time-of-day mode, but geography stays fixed.
      originPinnedRef.current = false;
      setSelectedVenue(null);
      setVenues([]);
      setMapVenues([]);
      setAnchorVenue(null);

      venuesLastFetchAtRef.current = 0;
      venuesCooldownUntilRef.current = 0;

      const deviceOrigin =
        deviceLocation &&
        typeof deviceLocation.latitude === 'number' &&
        typeof deviceLocation.longitude === 'number'
          ? deviceLocation
          : null;

      const neighborhoodOrigin =
        originLocation &&
        typeof originLocation.latitude === 'number' &&
        typeof originLocation.longitude === 'number'
          ? originLocation
          : null;

      // A category chosen after searching another city should keep that
      // city's discovery origin. Walking directions still use the phone.
      const deviceMarket = deviceOrigin
        ? getMarketForCoordinates(deviceOrigin.latitude, deviceOrigin.longitude)
        : null;
      const browsingAnotherMarket = Boolean(
        neighborhoodOrigin && deviceMarket && deviceMarket.id !== activeMarket?.id
      );
      const fetchOrigin = neighborhood
        ? (neighborhoodOrigin || deviceOrigin)
        : (browsingAnotherMarket ? neighborhoodOrigin : deviceOrigin);

      originPinnedRef.current = browsingAnotherMarket;
      if (!neighborhood && deviceOrigin && !browsingAnotherMarket) {
        setOriginLocation(deviceOrigin);
        setOutsideSupportedMarket(!isWithinSupportedMarket(deviceOrigin.latitude, deviceOrigin.longitude));
      }

      fetchVenues(cat, fetchOrigin, null, 'manual');
      setView('categories');
    };

    const handleNeighborhoodPick = async (name) => {
      const neighborhood = String(name || '').trim();
      if (!neighborhood) return;

      neighborhoodHintRef.current = neighborhood;
      setSelectedNeighborhood(neighborhood);
      originPinnedRef.current = false;

      // Neighborhood-only browsing uses the normal time-of-day category.
      const autoCategory = getAutoTimeCategory();
      setCategoryModeSafe('auto');
      setActiveCategorySafe(autoCategory);
      activeCategoryRef.current = autoCategory;

      setSearchQuery('');
      setSearchModeCategory(null);
      setSearchModeLabel(getCategoryModeLabel(autoCategory));
      setSearchScopeLabel(neighborhood);
      setSearchContextLabel(`📍 ${neighborhood} · Right Now`);
      setSelectedVenue(null);
      setVenues([]);
      setMapVenues([]);
      setAnchorVenue(null);

      // Reuse the existing backend neighborhood resolver so the frontend does
      // not maintain a second set of neighborhood center coordinates.
      let hoodCenter =
        originLocation &&
        typeof originLocation.latitude === 'number' &&
        typeof originLocation.longitude === 'number'
          ? originLocation
          : deviceLocation;

      try {
        const data = await fetchJSON(
          `/api/smart-search?q=${encodeURIComponent(neighborhood)}&marketId=${encodeURIComponent(activeMarket?.id || DEFAULT_MARKET_ID)}`,
          { cache: 'no-store' }
        );

        const hood = Array.isArray(data)
          ? data.find((item) => item?.isNeighborhood || item?.type === 'neighborhood')
          : null;

        const latitude = Number(hood?.coordinates?.latitude);
        const longitude = Number(hood?.coordinates?.longitude);

        if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
          hoodCenter = { latitude, longitude };
        }
      } catch (error) {
        console.warn(
          '[Neighborhood] center lookup failed; backend neighborhood filtering will still be used:',
          error
        );
      }

      if (
        hoodCenter &&
        typeof hoodCenter.latitude === 'number' &&
        typeof hoodCenter.longitude === 'number'
      ) {
        setOriginLocation(hoodCenter);
        setOutsideSupportedMarket(!isWithinSupportedMarket(hoodCenter.latitude, hoodCenter.longitude));
      }

      venuesLastFetchAtRef.current = 0;
      venuesCooldownUntilRef.current = 0;

      await fetchVenues(null, hoodCenter, null, 'auto');
      setView('categories');
    };

    const handleClearNeighborhood = async () => {
      neighborhoodHintRef.current = '';
      setSelectedNeighborhood('');
      originPinnedRef.current = false;

      const autoCategory = getAutoTimeCategory();
      setCategoryModeSafe('auto');
      setActiveCategorySafe(autoCategory);
      activeCategoryRef.current = autoCategory;

      setSearchQuery('');
      setSearchModeCategory(null);
      setSearchModeLabel('');
      setSearchScopeLabel('');
      setSearchContextLabel('📍 Nearby Right Now');
      setSelectedVenue(null);
      setVenues([]);
      setMapVenues([]);
      setAnchorVenue(null);

      const nearbyOrigin =
        deviceLocation &&
        typeof deviceLocation.latitude === 'number' &&
        typeof deviceLocation.longitude === 'number'
          ? deviceLocation
          : readLastLocation();

      if (nearbyOrigin) {
        setOriginLocation(nearbyOrigin);
        setOutsideSupportedMarket(!isWithinSupportedMarket(nearbyOrigin.latitude, nearbyOrigin.longitude));
      }

      venuesLastFetchAtRef.current = 0;
      venuesCooldownUntilRef.current = 0;

      await fetchVenues(null, nearbyOrigin, null, 'auto');
      setView('categories');
    };

    const handleLastVibe = () => {
      const lastVenue = readLastVenue();

      if (!lastVenue) {
        alert('No saved vibe yet. Tap a venue first.');
        return;
      }

      setSelectedVenue(lastVenue);
      setAnchorVenue(lastVenue);
        setMapVenues(venues);
      setMapDetailMode('venue');
      setView('map');
    };

    const handleMapViewFromCard = (venue, options = {}) => {
        saveListScroll();

        const isSearchContext = !!options.isSearchContext;

        trackAnalytics('map_open', analyticsVenuePayload(venue));

        setMapDetailMode(isSearchContext ? 'searchArea' : 'venue');
        setAnchorVenue(venue);
        setSelectedVenue(venue);
        setPartyMode(false);
        setMapVenues(venues);
        setView('map');
    };

    const handleEventMapOpen = (event) => {
      const latitude = Number(
        event?.coordinates?.latitude
      );

      const longitude = Number(
        event?.coordinates?.longitude
      );

      if (
        !Number.isFinite(latitude) ||
        !Number.isFinite(longitude)
      ) {
        alert(
          'This event does not have a usable map location.'
        );
        return;
      }

      const eventCategory = String(
        event?.category || ''
      ).toLowerCase();

      const eventEmoji = (() => {
        switch (eventCategory) {
          case 'music':
            return '🎵';
          case 'comedy':
            return '😂';
          case 'arts':
            return '🎨';
          case 'sports':
            return '🏟️';
          case 'family':
            return '🎈';
          case 'festival':
            return '🎉';
          default:
            return '🔥';
        }
      })();

      const eventId =
        event?.id ||
        event?.externalId ||
        `${event?.name || 'event'}-${event?.startDateTime || ''}`;

      const mapEvent = {
        id: `event:${eventId}`,
        place_id: `event:${eventId}`,
        name: event?.name || 'Event',
        venueName: event?.venueName || '',
        vicinity:
          event?.address ||
          event?.venueName ||
          '',
        address: event?.address || '',
        latitude,
        longitude,
        coordinates: {
          latitude,
          longitude,
        },
        geometry: {
          location: {
            lat: latitude,
            lng: longitude,
          },
        },
        emoji: eventEmoji,
        isEvent: true,
        type: 'event',
        category: 'event',
        eventCategory,
        eventSubcategory:
          event?.subcategory || '',
        startDateTime:
          event?.startDateTime || null,
        endDateTime:
          event?.endDateTime || null,
        eventUrl: event?.eventUrl || '',
        image:
          event?.imageUrl || '',
        description:
          event?.description || '',
        neighborhood:
          event?.neighborhood || '',
      };

      setEventsOpen(false);
      setExploreOpen(false);
      setSelectedVenue(mapEvent);
      setAnchorVenue(mapEvent);
      setVenues([mapEvent]);
      setMapVenues([mapEvent]);
      setMapDetailMode('event');
      setPartyMode(false);
      setSearchContextLabel(
        `🔥 ${event?.neighborhood || 'Happening Tonight'}`
      );
      setSearchModeLabel('Happening Tonight');
      setSearchScopeLabel(
        event?.venueName || event?.neighborhood || ''
      );
      setView('map');
    };

    const handleSavedVenueOpen = (venue) => {
      setSavedOpen(false);

      trackAnalytics(
        'favorite_open',
        analyticsVenuePayload(venue)
      );

      handleMapViewFromCard(venue);
    };

    const handleSavedEventMapOpen = (event) => {
      setSavedOpen(false);

      trackAnalytics(
        'favorite_open',
        analyticsEventPayload(event)
      );

      trackAnalytics(
        'event_map_open',
        analyticsEventPayload(event)
      );

      handleEventMapOpen(event);
    };

    const handleSavedEventDetails = (event) => {
      const url =
        String(
          event?.eventUrl || ''
        ).trim();

      if (!url) return;

      trackAnalytics(
        'favorite_open',
        analyticsEventPayload(event)
      );

      trackAnalytics(
        'event_external_open',
        analyticsEventPayload(event)
      );

      openExternal(url);
    };

    const handleMarkerClickOnMap = (venue) => {
      setSelectedVenue(venue);

      if (!venue?.isEvent) {
        saveLastVenue(venue);
      }
    };

    const geocodeAndBrowse = async () => {
      if (isWebPromoOnly) {
        window.location.href = APP_STORE_URL;
        return;
      }

      const q = searchQuery.trim();
      if (!q) return;
       
        const hornySearchRe =
          /\b(sex|sexual|porn|porno|pornography|escort|escorts|hookup|hookups|prostitute|prostitution|brothel|stripper|strippers|strip club|adult entertainment|adult store|sex shop|erotic|fetish|bdsm|nude|nudes|naked)\b/i;

        // PB_OAKLAND_UI_EDGES_V1: the Easter egg maps to a real SF address.
        if (hornySearchRe.test(q) && activeMarket?.id === DEFAULT_MARKET_ID) {
          const churchTarget = {
            name: 'Cathedral of St. Mary of the Assumption',
            latitude: 37.7842,
            longitude: -122.4255,
            address: '1111 Gough St, San Francisco, CA 94109',
          };

            const bonkVenue = {
              name: 'Cold Shower Club',
              rating: null,
              user_ratings_total: 0,
              vicinity:
                'BONK. Not that kind of party. Take a cold shower, then try tacos, burgers, smoothies, karaoke, cocktails, or a neighborhood.',
              address: churchTarget.address,
              type: 'food',
              types: ['easter_egg', 'place_of_worship'],
              place_id: 'party-button-cold-shower-club',
              open_now: true,
              price_level: null,
              coordinates: {
                latitude: churchTarget.latitude,
                longitude: churchTarget.longitude,
              },
              geometry: {
                location: {
                  lat: churchTarget.latitude,
                  lng: churchTarget.longitude,
                },
              },
              image: pickSafeFallback(6969),

                emoji: '🚿',
                displayRating: null,
                nudgedScore: null,
                partyScore: null,

                vibeTotal: null,
                vibeTopEmoji: null,
                vibeCounts: null,

                hideVibeSystem: true,
                partyScoreEmoji: '🚿',
                partyScoreLabel: 'Cold Shower Score',

              tags: ['BONK', 'Cold Shower', 'Try Tacos'],
              menu_url: null,
              universalSearch: true,
              searchMode: 'blocked-easter-egg',
              easterEgg: true,
              mapTargetName: churchTarget.name,
            };

          setCategoryModeSafe('manual');
          setActiveCategorySafe('food');
          activeCategoryRef.current = 'food';
          setSelectedVenue(bonkVenue);
          saveLastVenue(bonkVenue);
          setVenues([bonkVenue]);
          setMapVenues([]);
          setAnchorVenue(null);
            setSearchModeLabel('🚿 Intervention mode');
            setSearchScopeLabel('Blocked search');
            setSearchContextLabel('🚿 Spiritual Intervention Mode');
          setView('categories');

          requestAnimationFrame(() => {
            if (listScrollRef.current) listScrollRef.current.scrollTop = 0;
          });

          return;
        }
        
        // Send the user's original text to the backend. The backend owns
        // neighborhood parsing, intent classification, category selection, labels,
        // and strict-vs-broadened presentation.
        setSearchModeLabel('');
        setSearchScopeLabel('');

        let ac = null;
        let ownsSearchLoading = true;

        try {
          try { searchAbortRef.current?.abort?.(); } catch {}

          ac = new AbortController();
          searchAbortRef.current = ac;

          setIsSearchingNeighborhood(true);
          
          setMapVenues([]);
          setAnchorVenue(null);
          setSelectedVenue(null);
          setVenues([]);
          
          const origin =
          originLocation &&
          typeof originLocation.latitude === "number" &&
          typeof originLocation.longitude === "number"
            ? originLocation
            : deviceLocation;

          const searchSessionId = createSearchSessionId();
          const body = {
            query: q,
            clientQuery: q,
            searchSessionId,
            responseMode: "envelope",
            marketId: activeMarket?.id || DEFAULT_MARKET_ID,
            marketName: activeMarket?.name || '',
            latitude: origin?.latitude ?? null,
            longitude: origin?.longitude ?? null,
          };

          console.log("[Search] request →", BACKEND_BASE, body);
          
        const searchResponse = await fetchJSON("/api/search-venue", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Party-Uid": partyUid,
          },
          body: JSON.stringify(body),
          signal: ac.signal,
        });

        if (ac.signal.aborted) return;

        console.log("[Search] response ←", searchResponse);

        const searchMeta =
          !Array.isArray(searchResponse) && searchResponse?.search
            ? searchResponse.search
            : null;

        const responseMarket = getMarketById(searchMeta?.marketId);
        if (responseMarket && responseMarket.id !== activeMarketId) {
          setActiveMarketSafe(responseMarket.id);
        }

        const venuesFromSearch = Array.isArray(searchResponse)
          ? searchResponse
          : Array.isArray(searchResponse?.venues)
          ? searchResponse.venues
          : [];

        const authoritativeCategory = normalizeBackendSearchCategory(
          searchMeta,
          venuesFromSearch[0]
        );
        const authoritativeModeLabel = backendSearchModeLabel(
          searchMeta,
          authoritativeCategory
        );
        const authoritativeDisplayLabel = backendSearchDisplayLabel(
          searchMeta,
          q,
          venuesFromSearch[0]
        );
        const authoritativeScopeLabel = String(
          searchMeta?.scopeLabel || ''
        ).trim();
        const authoritativeNeighborhood = String(
          searchMeta?.neighborhoodDisplayName ||
            searchMeta?.neighborhood ||
            ''
        ).trim();

          if (!searchResponse?.blockedSearch && !venuesFromSearch.some((venue) => venue?.isMarket)) {
            const analyticsResultCount = venuesFromSearch.filter((venue) => !venue?.isMarket).length;

            trackAnalytics('search', {
              query: searchMeta?.query || q,
              marketId: responseMarket?.id || activeMarket?.id || DEFAULT_MARKET_ID,
              marketName: responseMarket?.name || activeMarket?.name || '',
              category:
                searchMeta?.effectiveCategory ||
                searchMeta?.displayCategory ||
                searchMeta?.effectiveType ||
                authoritativeCategory,
              neighborhood: authoritativeNeighborhood,
              searchLabel: authoritativeDisplayLabel,
              resultCount: analyticsResultCount,
              success: analyticsResultCount > 0,
              searchTraceId: searchMeta?.traceId || '',
              searchSessionId: searchMeta?.searchSessionId || searchSessionId,
              retrievalMode: searchMeta?.retrievalMode || '',
              broadeningMode: searchMeta?.broadeningMode || '',
              searchElapsedMs: searchMeta?.elapsedMs ?? null,
            });
          }

          if (searchResponse?.blockedSearch) {
            alert(
              searchResponse.message ||
                'BONK 🔨 Not that kind of party. Take a cold shower, then try coffee, tacos, burgers, smoothies, karaoke, cocktails, or a neighborhood.'
            );
            return;
          }
          
        if (!Array.isArray(venuesFromSearch) || venuesFromSearch.length === 0) {
          alert("No results for that search.");
          return;
        }

        const toPinnedShape = (v) => {
          const lat = v?.lat ?? v?.latitude ?? v?.coordinates?.latitude;
          const lng = v?.lng ?? v?.longitude ?? v?.coordinates?.longitude;

          return {
            ...v,
            latitude: typeof lat === "number" ? lat : (lat != null ? +lat : null),
            longitude: typeof lng === "number" ? lng : (lng != null ? +lng : null),
            address: v?.address || v?.formatted_address || v?.vicinity || v?.location || "",
            pinned: true,
          };
        };

        const best = venuesFromSearch[0];
          if (best?.isMarket && responseMarket) {
            setActiveMarketSafe(responseMarket.id);
            const nextOrigin = {
              latitude: Number(best.coordinates?.latitude),
              longitude: Number(best.coordinates?.longitude),
            };
            if (!Number.isFinite(nextOrigin.latitude) ||
                !Number.isFinite(nextOrigin.longitude)) return;
            neighborhoodHintRef.current = '';
            setSelectedNeighborhood('');
            originPinnedRef.current = true;
            setOriginLocation(nextOrigin);
            setOutsideSupportedMarket(false);
            setSelectedVenue(null);
            setMapVenues([]);
            setAnchorVenue(null);
            setSearchScopeLabel(responseMarket.name);
            setSearchContextLabel(`📍 ${responseMarket.name} · Right Now`);
            setCategoryModeSafe('auto');
            const autoCategory = getAutoTimeCategory();
            setActiveCategorySafe(autoCategory);
            activeCategoryRef.current = autoCategory;
            venuesLastFetchAtRef.current = 0;
            venuesCooldownUntilRef.current = 0;
            setView('categories');
            ownsSearchLoading = false;
            fetchVenues(null, nextOrigin, null, 'auto', responseMarket.id);
            return;
          }
          const isUniversalSearch = venuesFromSearch.some(
            (v) => v?.universalSearch || v?.searchMode === 'universal'
          );

          if (isUniversalSearch) {
              const cleanedSearchResults = cleanVenuesForDisplay(
                venuesFromSearch.filter((v) => !(v?.isNeighborhood || v?.type === 'neighborhood'))
              ).map((v, i) => withSafeVenueImage(v, i));

            if (!cleanedSearchResults.length) {
              alert('No open results for that search.');
              return;
            }

            const firstResult = cleanedSearchResults[0];
            const searchOrigin = firstResult?.searchOrigin || null;

            const nextOrigin =
              searchOrigin &&
              typeof searchOrigin.latitude === 'number' &&
              typeof searchOrigin.longitude === 'number'
                ? searchOrigin
                : origin;

            if (nextOrigin) {
              setOriginLocation(nextOrigin);
              setOutsideSupportedMarket(!isWithinSupportedMarket(nextOrigin.latitude, nextOrigin.longitude));
            }

            originPinnedRef.current = !!authoritativeNeighborhood;
            neighborhoodHintRef.current = authoritativeNeighborhood;
            setSelectedNeighborhood(authoritativeNeighborhood);

            const resultCategory =
              authoritativeCategory === 'neighborhood'
                ? normalizeBackendSearchCategory({}, firstResult)
                : authoritativeCategory;

            setCategoryModeSafe('manual');
            setActiveCategorySafe(resultCategory);
            activeCategoryRef.current = resultCategory;
            setSearchModeCategory(resultCategory);
            setSearchModeLabel(authoritativeModeLabel);
            setSearchScopeLabel(authoritativeScopeLabel);
            setSelectedVenue(firstResult);
            saveLastVenue(firstResult);
            setVenues(cleanedSearchResults);
            setMapVenues([]);
            setAnchorVenue(null);
            setSearchContextLabel(authoritativeDisplayLabel);
            setView('categories');

            requestAnimationFrame(() => {
              if (listScrollRef.current) listScrollRef.current.scrollTop = 0;
            });

            return;
          }
          const pinned = toPinnedShape(best);
        const isNeighborhood = !!(best?.isNeighborhood || best?.type === "neighborhood");

          setSearchModeLabel(authoritativeModeLabel);
          setSearchScopeLabel(authoritativeScopeLabel);
          setSearchContextLabel(authoritativeDisplayLabel);
          setSelectedVenue(pinned);

         

          if (isNeighborhood) {
            neighborhoodHintRef.current = best?.name || q;
            setSelectedNeighborhood(best?.name || q);
            originPinnedRef.current = false;

            const hoodCenter =
              typeof pinned.latitude === "number" && typeof pinned.longitude === "number"
                ? { latitude: pinned.latitude, longitude: pinned.longitude }
                : (origin || originLocation || deviceLocation);

            setOriginLocation(hoodCenter);
            setOutsideSupportedMarket(!isWithinSupportedMarket(hoodCenter.latitude, hoodCenter.longitude));

            setMapVenues([]);
            setAnchorVenue(null);

              const autoCategory = getAutoTimeCategory();
              setCategoryModeSafe('auto');
              setActiveCategorySafe(autoCategory);
              activeCategoryRef.current = autoCategory;
              setSearchModeCategory(null);

              const modeToUse = 'auto';
              const categoryToUse = null;

              venuesLastFetchAtRef.current = 0;
              venuesCooldownUntilRef.current = 0;

              setVenues([]);
              ownsSearchLoading = false;
              fetchVenues(categoryToUse, hoodCenter, null, modeToUse);
          } else {
            neighborhoodHintRef.current = '';
            setSelectedNeighborhood('');

            const newOrigin =
              typeof pinned.latitude === "number" && typeof pinned.longitude === "number"
                ? { latitude: pinned.latitude, longitude: pinned.longitude }
                : null;

            if (newOrigin) {
              originPinnedRef.current = true;
              setOriginLocation(newOrigin);
              setOutsideSupportedMarket(!isWithinSupportedMarket(newOrigin.latitude, newOrigin.longitude));
                venuesLastFetchAtRef.current = 0;
                venuesCooldownUntilRef.current = 0;

                const categoryToUse =
                  authoritativeCategory && authoritativeCategory !== 'neighborhood'
                    ? authoritativeCategory
                    : null;
                const modeToUse = categoryToUse ? 'manual' : 'auto';
                const nextCategory = categoryToUse || getAutoTimeCategory();

                setCategoryModeSafe(modeToUse);
                setActiveCategorySafe(nextCategory);
                activeCategoryRef.current = nextCategory;
                setSearchModeCategory(categoryToUse);

                const explicitPinnedVenue =
                  pinned?.isNeighborhood || pinned?.type === "neighborhood" ? null : pinned;

                setVenues(explicitPinnedVenue ? [explicitPinnedVenue] : []);
                ownsSearchLoading = false;
                fetchVenues(categoryToUse, newOrigin, explicitPinnedVenue, modeToUse);
            }
          }

        setView("categories");
        requestAnimationFrame(() => {
          if (listScrollRef.current) listScrollRef.current.scrollTop = 0;
        });

        } catch (err) {
          if (err?.name !== 'AbortError') {
            console.error("Error in search:", err, err?.bodyText);
            alert(`Search failed${err?.status ? ` (${err.status})` : ""}. Check console.`);
          }
        } finally {
          if (
            ownsSearchLoading &&
            ac &&
            searchAbortRef.current === ac
          ) {
            setIsSearchingNeighborhood(false);
          }
        }
    };

    const handleUseMyLocation = async () => {
      if (isWebPromoOnly) {
        window.location.href = APP_STORE_URL;
        return;
      }

      originPinnedRef.current = false;

      try {
        try { venuesAbortRef.current?.abort?.(); } catch {}
        try { searchAbortRef.current?.abort?.(); } catch {}

        const fresh = await getFreshLocation();
         
          writeLastLocation(fresh);
        setVenues([]);
        neighborhoodHintRef.current = '';
        setSelectedNeighborhood('');
        setMapDetailMode('venue');
        setDeviceLocation(fresh);
        setOriginLocation(fresh);
          setSelectedVenue(null);
        setMapVenues([]);
        setAnchorVenue(null);

          const autoCategory = getAutoTimeCategory();

          // Nearby should always mean “right now,” not the last manual category
          setCategoryModeSafe('auto');
          setActiveCategorySafe(autoCategory);
          activeCategoryRef.current = autoCategory;

          // defeat throttle/cooldown for an explicit reset
          venuesLastFetchAtRef.current = 0;
          venuesCooldownUntilRef.current = 0;

          setSearchQuery('');
          setSearchModeCategory(null);
          setSearchModeLabel('');
          setSearchScopeLabel('');
          setSearchContextLabel('📍 Nearby Right Now');

          const loadedVenues = await fetchVenues(null, fresh, null, 'auto');

          if (Array.isArray(loadedVenues) && loadedVenues.length > 0) {
            setSelectedVenue(loadedVenues[0]);
            saveLastVenue(loadedVenues[0]);
          }
      } catch (e) {
        console.warn('Fresh geolocation failed:', e);
        alert('Could not get your current location. Check permissions.');
      }
    };
    const onBackFromMap = () => {
        setMapVenues([]);
        setSelectedVenue(null);
        setAnchorVenue(null);
        setMapDetailMode('venue');
        setView('categories');
    };

    // ✅ Restore scroll whenever we return to the categories/list view
    useEffect(() => {
      if (view === 'categories') restoreListScroll();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [view, activeCategory, venues?.length]);

    useEffect(() => {
        const onFocus = async () => {
            if (view === 'slideshow') return;
            
            const myReqId = ++focusReqIdRef.current;
            
            try {
                const fresh = await getFreshLocation();
                if (myReqId !== focusReqIdRef.current) return; // ignore stale results
                
                setDeviceLocation(fresh);
                writeLastLocation(fresh);
                
                const was = originRef.current;
                
                const movedFar =
                was?.latitude && was?.longitude
                ? metersBetween(
                                { lat: was.latitude, lng: was.longitude },
                                { lat: fresh.latitude, lng: fresh.longitude }
                                ) > 250
                : true;
                
                if (movedFar && !originPinnedRef.current && !neighborhoodHintRef.current) {
                    setOriginLocation(fresh);
                    setSelectedVenue(null);
                    setMapVenues([]);
                    setAnchorVenue(null);
                    
                    if (categoryModeRef.current === 'auto') {
                        fetchVenues(null, fresh, null, 'auto');
                    } else {
                        fetchVenues(activeCategoryRef.current, fresh, null, 'manual');
                    }
                }
                
            } catch (e) {
                console.warn('[location] focus refresh failed', e);
            }
        };
        
        const onVisibility = () => {
            if (document.visibilityState === 'visible') onFocus();
        };
        
        window.addEventListener('focus', onFocus);
        document.addEventListener('visibilitychange', onVisibility);
        
        return () => {
            window.removeEventListener('focus', onFocus);
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, [view]);

    const emptyStateCategory =
      searchModeCategory ||
      (categoryMode === 'auto'
        ? getAutoTimeCategory()
        : activeCategory);

    const emptyStateMessages = getLoadingMessages(emptyStateCategory);

    const emptyStateLoadingMessage =
      emptyStateMessages[
        loadingMessageIndex % Math.max(1, emptyStateMessages.length)
      ];

    const emptyStateDoneTitle = (() => {
      switch (emptyStateCategory) {
        case 'coffee':
          return 'No open coffee spots found';
        case 'brunch':
          return 'No open brunch spots found';
        case 'bars':
          return 'No open bars found';
        case 'clubs':
          return 'No nightlife found right now';
        case 'late_night':
          return 'No late-night spots found';
        case 'food':
          return 'No open food spots found';
        case 'hidden_gems':
          return 'No hidden gems found right now';
        default:
          return 'No spots found right now';
      }
    })();

    const emptyStateDoneSubtitle =
      'Try Search, Explore, or another category.';

    return (
      <div className={`App ${isNative ? 'native' : ''}`} style={frameStyle}>
            {view === 'slideshow' ? (
              <div className="party-button-screen">
                                     {locationIntro && (
                                       <div className="location-intro-overlay">
                                         <div className="location-intro-card">
                                           <div className="location-intro-icon">📍</div>
                                           <div className="location-intro-title">Finding nearby vibes…</div>
                                           <div className="location-intro-subtitle">
                                             The Party Button uses your location to find what’s closest right now.
                                           </div>
                                         </div>
                                       </div>
                                     )}
                                     
                                     <PartyButton
                  onClick={async () => {
                    if (isWebPromoOnly) {
                      window.location.href = APP_STORE_URL;
                      return;
                    }

                      trackAnalytics('party_button_press');

                      setPartyMode(true);
                      setCategoryModeSafe('auto');

                      const autoCategory = getAutoTimeCategory();
                      setActiveCategorySafe(autoCategory);
                      activeCategoryRef.current = autoCategory;

                      setIsSearchingNeighborhood(true);
                      setLocationIntro(true);
                      await new Promise((resolve) => setTimeout(resolve, 450));
                      
                      try {
                          const fresh = await getFreshLocation();
                          
                          
                          
                          writeLastLocation(fresh);
                          
                          originPinnedRef.current = false;
                          neighborhoodHintRef.current = '';
                          setSelectedNeighborhood('');
                          setSelectedVenue(null);
                          setVenues([]);
                          setMapVenues([]);
                          setAnchorVenue(null);
                          setSearchQuery('');
                          setSearchModeCategory(null);
                          setSearchModeLabel('');
                          setSearchScopeLabel('');
                          setDeviceLocation(fresh);
                          setOriginLocation(fresh);
                          setOutsideSupportedMarket(!isWithinSupportedMarket(fresh.latitude, fresh.longitude));
                          
                          venuesLastFetchAtRef.current = 0;
                          venuesCooldownUntilRef.current = 0;
                          
                          const loadedVenues = await fetchVenues(null, fresh, null, 'auto');

                          const partyButtonResults =
                            Array.isArray(loadedVenues)
                              ? loadedVenues
                              : [];

                          const firstPartyButtonVenue =
                            partyButtonResults[0] || null;

                          trackAnalytics(
                            'party_button_result',
                            {
                              ...(firstPartyButtonVenue
                                ? analyticsVenuePayload(firstPartyButtonVenue)
                                : {}),
                              category: autoCategory,
                              resultCount: partyButtonResults.length,
                              success: partyButtonResults.length > 0,
                            }
                          );

                          if (partyButtonResults.length > 0) {
                              setSelectedVenue(firstPartyButtonVenue);
                              saveLastVenue(firstPartyButtonVenue);
                          }
                          
                          await maybeAskForReview();

                          setView('map');
                          setLocationIntro(false);

                      } catch (e) {
                      const fallbackLoc =
                        readLastLocation() || {
                          latitude: 37.7749,
                          longitude: -122.4194,
                        };

                      originPinnedRef.current = false;
                      neighborhoodHintRef.current = '';
                      setSelectedNeighborhood('');
                      setSelectedVenue(null);
                      setMapVenues([]);
                      setAnchorVenue(null);
                      setSearchQuery('');
                      setSearchModeCategory(null);
                      setSearchModeLabel('');
                      setSearchScopeLabel('');
                      setDeviceLocation(fallbackLoc);
                      setOriginLocation(fallbackLoc);
                      setOutsideSupportedMarket(
                        !isWithinSupportedMarket(
                          fallbackLoc.latitude,
                          fallbackLoc.longitude
                        )
                      );

                      venuesLastFetchAtRef.current = 0;
                      venuesCooldownUntilRef.current = 0;

                        const fallbackVenues =
                          await fetchVenues(
                            null,
                            fallbackLoc,
                            null,
                            'auto'
                          );

                        const partyButtonFallbackResults =
                          Array.isArray(fallbackVenues)
                            ? fallbackVenues
                            : [];

                        const firstFallbackVenue =
                          partyButtonFallbackResults[0] || null;

                        trackAnalytics(
                          'party_button_result',
                          {
                            ...(firstFallbackVenue
                              ? analyticsVenuePayload(firstFallbackVenue)
                              : {}),
                            category: autoCategory,
                            resultCount:
                              partyButtonFallbackResults.length,
                            success:
                              partyButtonFallbackResults.length > 0,
                          }
                        );

                        if (partyButtonFallbackResults.length > 0) {
                          setSelectedVenue(firstFallbackVenue);
                          saveLastVenue(firstFallbackVenue);
                        }

                        setView('map');
                          setLocationIntro(false);
                      }
                  }}
                />

                                     <div
                                       style={{
                                         position: 'fixed',
                                         top: '68dvh',
                                         left: '50%',
                                         transform: 'translateX(-50%)',
                                         width: '90%',
                                         textAlign: 'center',
                                         fontSize: '22px',
                                         fontWeight: 800,
                                         color: '#d51b91',
                                         textShadow: '0 0 18px rgba(255, 92, 190, 0.45)',
                                         zIndex: 999999,
                                         pointerEvents: 'none',
                                       }}
                                     >
                                       Find the vibe before you arrive.
                                     </div>

                                     {/* APPLE_SUPPORT_LINKS_V1 */}
                                     {isWebPromoOnly && (
                                       <nav
                                         aria-label="Website links"
                                         style={{
                                           position: 'fixed',
                                           left: '50%',
                                           bottom:
                                             'calc(22px + env(safe-area-inset-bottom))',
                                           transform: 'translateX(-50%)',
                                           display: 'flex',
                                           alignItems: 'center',
                                           justifyContent: 'center',
                                           gap: '10px',
                                           width: '90%',
                                           zIndex: 1000000,
                                           fontSize: '15px',
                                           fontWeight: 700,
                                         }}
                                       >
                                         <a
                                           href="/support"
                                           style={{
                                             color: '#d51b91',
                                             textDecoration: 'underline',
                                           }}
                                         >
                                           App Support
                                         </a>

                                         <span
                                           aria-hidden="true"
                                           style={{
                                             color: '#8f8f8f',
                                           }}
                                         >
                                           ·
                                         </span>

                                         <a
                                           href="/privacy"
                                           style={{
                                             color: '#d51b91',
                                             textDecoration: 'underline',
                                           }}
                                         >
                                           Privacy Policy
                                         </a>
                                       </nav>
                                     )}

              </div>
            ) : (
          <div style={safeAreaStyle}>
            {outsideSupportedMarket && (
              <div className="sf-launch-modal">
                <div className="sf-launch-modal-content">
                  {/* PB_OAKLAND_BETA_UI_COPY_V1 */}
                  <h2>{getMarketById('oakland')
                    ? '🎉 The Party Button is live in San Francisco and Oakland beta'
                    : '🎉 The Party Button is live in San Francisco'}</h2>
                  <p>{getMarketById('oakland')
                    ? 'You can browse supported Oakland beta neighborhoods or start in San Francisco.'
                    : 'We’re currently focused on SF to keep the experience lively. You can browse San Francisco venues now — more cities coming soon.'}</p>
                  <button onClick={() => browseSupportedMarket(DEFAULT_MARKET_ID)}>
                    Browse San Francisco
                  </button>
                  {getMarketById('oakland') && (
                    <button onClick={() => browseSupportedMarket('oakland')}>
                      Browse Oakland beta
                    </button>
                  )}
                </div>
              </div>
            )}
    

                 {view === 'categories' && (
                   <div className="mobile-stack-layout">

                                            <div className="mobile-search-panel">
                                              <input
                                                type="text"
                                                value={searchQuery}
                                                onChange={(e) => setSearchQuery(e.target.value)}
                                            placeholder={`Search anything: smoothie, burger, ${activeMarket?.exploreNeighborhoods?.[0] || "neighborhood"}...`}
                                            aria-label="Search anything: smoothie, burger, neighborhood"
                                                onKeyDown={(e) => {
                                                  if (e.key === 'Enter') geocodeAndBrowse();
                                                }}
                                              />

                                            <div className="mobile-action-row primary-action-row">
                                              <button
                                                className="category-button"
                                                onClick={geocodeAndBrowse}
                                                disabled={isSearchingNeighborhood}
                                              >
                                            {isSearchingNeighborhood ? 'Searching…' : '🔎 Search'}
                                              </button>

                                              <button
                                                className="category-button"
                                                onClick={handleUseMyLocation}
                                                disabled={isSearchingNeighborhood}
                                              >
                                            {isSearchingNeighborhood ? 'Searching…' : '📍 Nearby'}
                                              </button>

                                              <button
                                                className="category-button"
                                                onClick={() => {
                                                  setExploreOpen(false);
                                                  setTimeout(() => setExploreOpen(true), 10);
                                                }}
                                              >
                                            ✨ Explore
                                              </button>
                                            

                                            <details className="secondary-actions-menu compact-more-menu">
                                                                                          <summary
                                                                                            className="secondary-actions-trigger"
                                                                                            aria-label="More actions"
                                                                                            title="More actions"
                                                                                          >
                                            <span
                                              className="vertical-more-icon"
                                              aria-hidden="true"
                                            >
                                              <span className="more-dot" />
                                              <span className="more-dot" />
                                              <span className="more-dot" />
                                            </span>
                                                                                          </summary>

                                                                                          <div
                                                                                            className="secondary-actions-popover"
                                                                                            role="menu"
                                                                                            aria-label="More list actions"
                                                                                          >
                                                                                            <button
                                                                                              className="secondary-menu-item"
                                                                                              type="button"
                                                                                              role="menuitem"
                                                                                              onClick={(event) => {
                                                                                                const menu = event.currentTarget.closest('details');
                                                                                                if (menu) menu.removeAttribute('open');
                                                                                                setSavedOpen(true);
                                                                                              }}
                                                                                            >
                                                                                              <span className="secondary-menu-icon" aria-hidden="true">♥</span>
                                                                                              <span className="secondary-menu-copy">
                                                                                                <strong>Saved</strong>
                                                                                                <small>
                                                                                                  {savedVenues.length + savedEvents.length
                                                                                                    ? `${savedVenues.length + savedEvents.length} saved`
                                                                                                    : 'Spots and events you want to keep'}
                                                                                                </small>
                                                                                              </span>
                                                                                            </button>

                                                                                            <button
                                                                                              className="secondary-menu-item"
                                                                                              type="button"
                                                                                              role="menuitem"
                                                                                              onClick={(event) => {
                                                                                                const menu = event.currentTarget.closest('details');
                                                                                                if (menu) menu.removeAttribute('open');
                                                                                                handleLastVibe();
                                                                                              }}
                                                                                            >
                                                                                              <span className="secondary-menu-icon" aria-hidden="true">↩️</span>
                                                                                              <span className="secondary-menu-copy">
                                                                                                <strong>Last Vibe</strong>
                                                                                                <small>Return to your most recent pick</small>
                                                                                              </span>
                                                                                            </button>

                                                                                            <button
                                                                                              className="secondary-menu-item"
                                                                                              type="button"
                                                                                              role="menuitem"
                                                                                              onClick={(event) => {
                                                                                                const menu = event.currentTarget.closest('details');
                                                                                                if (menu) menu.removeAttribute('open');
                                                                                                handleWildCard();
                                                                                              }}
                                                                                            >
                                                                                              <span className="secondary-menu-icon" aria-hidden="true">🎲</span>
                                                                                              <span className="secondary-menu-copy">
                                                                                                <strong>Random</strong>
                                                                                                <small>Pick one from the current list</small>
                                                                                              </span>
                                                                                            </button>

                                                                                            {marketFeatures.restrooms === true && (
                                                                                              <button
                                                                                                className="secondary-menu-item"
                                                                                                type="button"
                                                                                                role="menuitem"
                                                                                                onClick={(event) => {
                                                                                                  const menu = event.currentTarget.closest('details');
                                                                                                  if (menu) menu.removeAttribute('open');
                                                                                                  handlePottyButton();
                                                                                                }}
                                                                                              >
                                                                                                <span className="secondary-menu-icon" aria-hidden="true">🚻</span>
                                                                                                <span className="secondary-menu-copy">
                                                                                                  <strong>The Potty Button</strong>
                                                                                                  <small>Find nearby restrooms</small>
                                                                                                </span>
                                                                                              </button>
                                                                                            )}

                                                                                            {marketFeatures.election === true && (
                                                                                              <button
                                                                                                className="secondary-menu-item"
                                                                                                type="button"
                                                                                                role="menuitem"
                                                                                                onClick={(event) => {
                                                                                                  const menu = event.currentTarget.closest('details');
                                                                                                  if (menu) menu.removeAttribute('open');
                                                                                                  setElectionOpen(true);
                                                                                                }}
                                                                                              >
                                                                                                <span className="secondary-menu-icon" aria-hidden="true">🗳️</span>
                                                                                                <span className="secondary-menu-copy">
                                                                                                  <strong>Election 2026</strong>
                                                                                                  <small>Rate SF Supervisor candidates</small>
                                                                                                </span>
                                                                                              </button>
                                                                                            )}

                                                                                            <button
                                                                                              className="secondary-menu-item"
                                                                                              type="button"
                                                                                              role="menuitem"
                                                                                              onClick={(event) => {
                                                                                                const menu = event.currentTarget.closest('details');
                                                                                                if (menu) menu.removeAttribute('open');
                                                                                                handleShareApp();
                                                                                              }}
                                                                                            >
                                                                                              <span className="secondary-menu-icon" aria-hidden="true">📲</span>
                                                                                              <span className="secondary-menu-copy">
                                                                                                <strong>Share</strong>
                                                                                                <small>Send The Party Button to a friend</small>
                                                                                              </span>
                                                                                            </button>
                                                                                          </div>
                                                                                        </details>
</div>

                                              {marketFeatures.partyQuest === true && partyQuestPromo && (
                                                <button
                                                  type="button"
                                                  className="party-quest-main-entry"
                                                  onClick={() => setPartyQuestOpen(true)}
                                                >
                                                  <span
                                                    className="party-quest-main-entry-icon"
                                                    aria-hidden="true"
                                                  >
                                                    🎯
                                                  </span>

                                                  <span className="party-quest-main-entry-copy">
                                                    <strong>Party Quest</strong>

                                                    <small>
                                                      Neighborhood adventures around San Francisco
                                                    </small>
                                                  </span>

                                                  <span
                                                    className="party-quest-main-entry-arrow"
                                                    aria-hidden="true"
                                                  >
                                                    ›
                                                  </span>
                                                </button>
                                              )}

                                              {marketFeatures.events === true && (
                                                <button
                                                  type="button"
                                                  className="happening-tonight-entry"
                                                  onClick={openEvents}
                                                >
                                                  <span
                                                    className="happening-tonight-entry-icon"
                                                    aria-hidden="true"
                                                  >
                                                    🔥
                                                  </span>

                                                  <span className="happening-tonight-entry-copy">
                                                    <strong>Happening Tonight</strong>
                                                    <small>
                                                      Music, comedy, festivals & more around San Francisco
                                                    </small>
                                                  </span>

                                                  <span
                                                    className="happening-tonight-entry-arrow"
                                                    aria-hidden="true"
                                                  >
                                                    ›
                                                  </span>
                                                </button>
                                              )}

                                            </div>
                                            <div className="time-mode-pill">
                                              {searchModeLabel ||
                                                getCategoryModeLabel(
                                                  searchModeCategory ||
                                                    (categoryMode === 'auto'
                                                      ? getAutoTimeCategory()
                                                      : activeCategory)
                                                )}
                                            </div>
                                            {selectedNeighborhood && (
                                              <div
                                                style={{
                                                  display: 'flex',
                                                  justifyContent: 'center',
                                                  margin: '8px 0 2px',
                                                }}
                                              >
                                                <button
                                                  type="button"
                                                  onClick={handleClearNeighborhood}
                                                  aria-label={`Clear ${selectedNeighborhood} neighborhood filter`}
                                                  style={{
                                                    border: '1px solid rgba(213,27,145,.25)',
                                                    background: 'rgba(213,27,145,.08)',
                                                    color: '#b01876',
                                                    borderRadius: 999,
                                                    padding: '7px 11px',
                                                    fontSize: 12,
                                                    fontWeight: 900,
                                                    cursor: 'pointer',
                                                  }}
                                                >
                                                  📍 {selectedNeighborhood} ×
                                                </button>
                                              </div>
                                            )}
                                            <div className="section-heading-row">
                                            <div className="section-heading-title">
                                              {searchContextLabel}
                                            </div>

                                            <div className="section-heading-subtitle">
                                            {searchScopeLabel ||
                                              (searchContextLabel.toLowerCase().includes('search')
                                                ? 'Exploring around your search'
                                                : searchContextLabel.toLowerCase().includes('nearby')
                                                ? 'Using your current location'
                                                : 'Sorted by vibe, distance, and what’s open')}
                                            </div>
                                            </div>
                                            <div className="mobile-venue-list"
                       ref={listScrollRef}
                       onScroll={handleListScroll}
                     >
                       <div className={`venue-scroll-area ${venues.length === 0 ? 'is-empty' : ''}`}>
                         {venues.length > 0 ? (
                           venues.map((v, i) => {
                             const vid = v.place_id || v.placeId || v.id || v.name || `venue_${i}`;
                             const selectedId =
                               selectedVenue?.place_id ||
                               selectedVenue?.placeId ||
                               selectedVenue?.id ||
                               selectedVenue?.name;

                             const isSelected = !!(selectedId && vid && selectedId === vid);

                             return (
                               <div
                                 key={vid}
                                 role="button"
                                 tabIndex={0}
                                 className={isSelected ? 'venue-card-shell is-selected' : 'venue-card-shell'}
                                     onClick={() => {
                                       if (!v?.pinned) {
                                         trackAnalytics(
                                           'venue_select',
                                           analyticsVenuePayload(v)
                                         );
                                         if (isSponsoredVenue(v)) {
                                           trackAnalytics(
                                             'featured_open',
                                             analyticsVenuePayload(v)
                                           );
                                         }
                                         setSelectedVenue(v);
                                         saveLastVenue(v);
                                       }
                                     }}
                                 onKeyDown={(e) => {
                                     if ((e.key === 'Enter' || e.key === ' ') && !v?.pinned) {
                                       trackAnalytics(
                                         'venue_select',
                                         analyticsVenuePayload(v)
                                       );
                                       if (isSponsoredVenue(v)) {
                                         trackAnalytics(
                                           'featured_open',
                                           analyticsVenuePayload(v)
                                         );
                                       }
                                       setSelectedVenue(v);
                                       saveLastVenue(v);
                                     }
                                 }}
                               >
                                
                                    {!v?.pinned && (
                                      <button
                                        type="button"
                                        className={`venue-save-fab ${isVenueSaved(v) ? 'is-saved' : ''}`}
                                        aria-label={
                                          isVenueSaved(v)
                                            ? `Remove ${v?.name || 'venue'} from Saved`
                                            : `Save ${v?.name || 'venue'}`
                                        }
                                        title={
                                          isVenueSaved(v)
                                            ? 'Remove from Saved'
                                            : 'Save this spot'
                                        }
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          toggleSavedVenue(v);
                                        }}
                                      >
                                        {isVenueSaved(v)
                                          ? '♥'
                                          : '♡'}
                                      </button>
                                    )}
                                    
                                     <VenueCard
                                   venue={v}
                                   onMapClick={(venue, options) => handleMapViewFromCard(venue, options)}
                                   backendBase={BACKEND_BASE}
                                   partyUid={partyUid}
                                   fallbackPool={artistOnlyPool.length ? artistOnlyPool : combinedFallbackPool}
                                   onFeaturedImpression={handleFeaturedImpression}
                                 />
                               </div>
                             );
                           })
                         ) : (
                           <div className="venue-empty-wrap">
                             <div className="venue-empty-bg" />
                             <div className="venue-empty-overlay" />

                              <div className="venue-empty-state">
                                <div className="venue-empty-icon">
                                  {isSearchingNeighborhood ? '📍' : '🫥'}
                                </div>

                                <div className="venue-empty-title">
                                  {isSearchingNeighborhood
                                    ? getCategoryModeLabel(emptyStateCategory)
                                    : emptyStateDoneTitle}
                                </div>

                                <div className="venue-empty-subtitle">
                                  {isSearchingNeighborhood
                                    ? emptyStateLoadingMessage
                                    : emptyStateDoneSubtitle}
                                </div>
                              </div>
                           </div>
                         )}
                       </div>
                     </div>

                   </div>
                 )}
                 
                 {exploreOpen && (
                                          <ExploreSheet
                                            onPickCategory={(cat) => {
                                              handleCategoryClick(cat);
                                              setExploreOpen(false);
                                            }}
                                            neighborhoods={activeMarket?.exploreNeighborhoods || []}
                                            marketName={activeMarket?.name || "San Francisco"}
                                            isBetaMarket={activeMarket?.id === "oakland"}
                                            features={marketFeatures}
                                            selectedNeighborhood={selectedNeighborhood}
                                            onPickNeighborhood={(name) => {
                                              handleNeighborhoodPick(name);
                                              setExploreOpen(false);
                                            }}
                                            onClearNeighborhood={() => {
                                              handleClearNeighborhood();
                                              setExploreOpen(false);
                                            }}
                                            onOpenPartyQuest={() => {
                                              setExploreOpen(false);
                                              setPartyQuestOpen(true);
                                            }}
                                            onOpenEvents={() => {
                                              setExploreOpen(false);
                                              openEvents();
                                            }}
                                            onClose={() => setExploreOpen(false)}
                                            onOpenUrl={openExternal}
                                            featuredArtist={featuredArtist}
                                            backendBase={BACKEND_BASE}
                                            partyUid={partyUid}
                                            selectedVenue={selectedVenue}
                                            onClaimSubmitted={handleClaimSubmitted}
                                          />
                                        )}

                                        {view === 'headlines' && (
                                          <div className="headline-container full-screen">
                                            <HeadlineFeed
                                              backendBase={BACKEND_BASE}
                                              onBack={() => setView('categories')}
                                            />
                                          </div>
                                        )}

                                        {savedOpen && (
                                          <SavedSheet
                                            savedVenues={savedVenues}
                                            savedEvents={savedEvents}
                                            onClose={() => setSavedOpen(false)}
                                            onOpenVenue={handleSavedVenueOpen}
                                            onOpenEventMap={handleSavedEventMapOpen}
                                            onOpenEventDetails={handleSavedEventDetails}
                                            onRemoveVenue={toggleSavedVenue}
                                            onRemoveEvent={toggleSavedEvent}
                                          />
                                        )}

                                        {marketFeatures.partyQuest === true && partyQuestOpen && (
                                          <PartyQuestSheet
                                            backendBase={BACKEND_BASE}
                                            partyUid={partyUid}
                                            onClose={() => setPartyQuestOpen(false)}
                                            onOpenUrl={openExternal}
                                          />
                                        )}

                                        {marketFeatures.events === true && eventsOpen && (
                                          <HappeningTonightSheet
                                            onClose={() => setEventsOpen(false)}
                                            onOpenUrl={openExternal}
                                            onShowEventOnMap={(event) => {
                                              trackAnalytics(
                                                'event_map_open',
                                                analyticsEventPayload(event)
                                              );
                                              handleEventMapOpen(event);
                                            }}
                                            onEventSelect={handleEventSelect}
                                            onEventExternalOpen={handleEventExternalOpen}
                                            isEventSaved={isEventSaved}
                                            onToggleSavedEvent={toggleSavedEvent}
                                            deviceLocation={deviceLocation}
                                          />
                                        )}

                                        {marketFeatures.election === true && electionOpen && (
                                          <Election2026Sheet
                                            partyUid={partyUid}
                                            onClose={() => setElectionOpen(false)}
                                            onOpenUrl={openExternal}
                                          />
                                        )}

                                        {view === 'map' && (
                                          <MapView
                                            venues={mapVenues.length ? mapVenues : venues}
                                            userLocation={originLocation}
                                            deviceLocation={deviceLocation}
                                            selectedVenue={selectedVenue}
                                            anchorVenue={anchorVenue}
                                            partyMode={partyMode}
                                            onMarkerClick={handleMarkerClickOnMap}
                                            setView={setView}
                                            mapboxApiKey={mapboxApiKey}
                                            backendBase={BACKEND_BASE}
                                            onBackFromMap={onBackFromMap}
                                            onOpenUrl={handleMapOpenUrl}
                                            onRouteView={handleRouteView}
                                            partyUid={partyUid}
                                            onClaimSubmitted={handleClaimSubmitted}
                                            mapDetailMode={mapDetailMode}
                                          />
                                        )}
                                              </div>
                                            )}
                                          </div>
                                        );
                                        }

                                        export default App;
